// Clients list, detail (services + edit + config), Add wizard.

import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { I } from '../components/icons';
import {
  Avatar, ResourceBar, StatusBadge, StatusDot, Modal, Stepper, ServiceIcon,
  Empty, useToast, KV, KVrow,
} from '../components/ui';
import { ConfigEditor } from '../components/editor';
import { useOperatorMode } from '../hooks/useOperatorMode';
import { onVisible } from '../hooks/onVisible';
import { PLANS } from '../data/mock';
import {
  createTenant, deleteTenant, fetchTenants, fetchTenant, updateTenant, updateService, restartService,
  suspendTenant, resumeTenant,
  fetchAPITokens, createAPIToken, revokeAPIToken,
  fetchAudit, fetchServiceLogs, addService, deleteService, fetchArgocdStatus, fetchLiveConfig,
  fetchChartCatalog, fetchNodes, setServiceIngress, clearServiceIngress,
  fetchBasicUsers, createBasicUser, deleteBasicUser, updateBasicUserNote, updateBasicUserPassword,
  fetchGitOpsHistory, fetchServiceImageTags,
  listAssetVersions, uploadAssetVersion, deleteAssetVersion,
  fetchPVCUsage,
  fetchS3Configs, createS3Config, updateS3Config, deleteS3Config,
  provisionS3Bucket, fetchBucketUsage, setBucketQuota, rotateS3BucketKeys, fetchS3Credentials,
  fetchStorageServers, type StorageServer,
  type ProvisionedBucket, type BucketUsage,
  fetchBasicUserS3Grants, setBasicUserS3Grants, type S3Grant,
  fetchS3ConfigGrants, setS3ConfigGrants, type S3ConfigGrant,
  fetchServiceAccess, setUserServiceAccess,
  fetchS3Accounts, fetchS3ConfigAccountGrants, setS3ConfigAccountGrants,
  type S3Account, type S3ConfigAccountGrant,
  ApiError,
  type APIToken, type CreateAPITokenResponse, type AuditEvent, type ChartCatalogEntry,
  type NodeStatus, type BasicUser, type ImageTags, type AssetVersion, type PVCUsage,
  type S3Config, type S3ConfigInput,
} from '../api/client';
import type { AppState, AssetSlot, Client, ClientService, Plan, Route } from '../types';

// Asset version-name grammar — mirrors the backend assetVersionPattern.
const ASSET_VERSION_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

// UsageCell renders one resource-usage tile (CPU / Memory / Disk / GPU) with a
// uniform value + bar + caption structure so every tile lines up on a row.
// pct < 0 renders the bar as an n/a track.
function UsageCell({ label, head, pct, sub }: { label: string; head: string; pct: number; sub: string }) {
  return (
    <div className="usage-cell">
      <div className="metric-label" style={{ marginBottom: 6 }}>{label}</div>
      <div className="mono usage-head">{head}</div>
      <div className="usage-bar"><ResourceBar value={pct}/></div>
      <div className="usage-sub">{sub}</div>
    </div>
  );
}


type EditState = {
  replicas: number;
  cpuLimit: string;
  memLimit: string;
  nodeName: string;
  imageTag: string;
} | null;

// Docker/OCI image tag grammar — mirrors the backend imageTagPattern. An empty
// string is allowed (clears the override; reverts to the chart default).
const IMAGE_TAG_RE = /^[A-Za-z0-9_][A-Za-z0-9._-]{0,127}$/;

// ─── CLIENTS LIST ───────────────────────────────────────────────────────────
export function ClientsList({
  state, setState, onNav, openAddWizard, loading = false,
}: { state: AppState; setState: React.Dispatch<React.SetStateAction<AppState>>; onNav: (r: Route) => void; openAddWizard: () => void; loading?: boolean }) {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [syncing, setSyncing] = useState(false);

  const filtered = state.clients.filter((c) => {
    if (q && !c.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (planFilter !== 'all' && c.plan !== planFilter) return false;
    return true;
  });

  // Re-fetch the tenants list. The /api/v1/tenants endpoint already enriches
  // each row with kubectl-derived status, so a re-fetch is effectively
  // "sync from cluster" for the dashboard's purposes.
  const doRefresh = async () => {
    setSyncing(true);
    try {
      const clients = await fetchTenants();
      setState((s) => ({ ...s, clients }));
      toast?.success?.(`Refreshed ${clients.length} clients`);
    } catch (err) {
      toast?.error?.(err instanceof ApiError ? err.message : 'Refresh failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="page-narrow">
      <div className="page-head spread">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="page-sub">{state.clients.length} tenants · each provisioned through a forked git repo synced by ArgoCD.</p>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" onClick={doRefresh} disabled={syncing} title="Re-read every client's live status from the cluster">
            <I.Refresh size={13}/> {syncing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button className="btn btn-primary" onClick={openAddWizard}>
            <I.Plus size={14}/> New client
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="row" style={{ gap: 8 }}>
            <div style={{ position: 'relative', width: 280 }}>
              <I.Search size={13} style={{ position: 'absolute', left: 9, top: 7, color: 'var(--text-3)' }}/>
              <input className="input" style={{ paddingLeft: 30, height: 28, fontSize: 12 }}
                     placeholder="Search clients…" value={q} onChange={(e) => setQ(e.target.value)}/>
            </div>
            <select className="select" style={{ height: 28, width: 'auto', paddingRight: 26, fontSize: 12 }}
                    value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All status</option>
              <option value="ready">Ready</option>
              <option value="provisioning">Provisioning</option>
              <option value="degraded">Degraded</option>
              <option value="failing">Failing</option>
              <option value="suspended">Suspended</option>
            </select>
            <select className="select" style={{ height: 28, width: 'auto', paddingRight: 26, fontSize: 12 }}
                    value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
              <option value="all">All plans</option>
              {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <span className="muted" style={{ fontSize: 12 }}>{filtered.length} of {state.clients.length}</span>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Client</th>
              <th>Plan</th>
              <th>Services</th>
              <th>Status</th>
              <th>Avg CPU</th>
              <th>Avg Mem</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && state.clients.length === 0 && (
              <tr><td colSpan={8} className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 12.5 }}>Loading clients…</td></tr>
            )}
            {!loading && state.clients.length === 0 && (
              <tr><td colSpan={8} className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 12.5 }}>No clients yet — create the first one with <b>New client</b>.</td></tr>
            )}
            {state.clients.length > 0 && filtered.length === 0 && (
              <tr><td colSpan={8} className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 12.5 }}>No clients match the current filters.</td></tr>
            )}
            {filtered.map((c) => {
              // Average only measured services (usage >= 0); -1 = n/a sentinel.
              const avgOf = (pick: (s: ClientService) => number) => {
                const m = c.services.filter((s) => pick(s) >= 0);
                return m.length ? Math.round(m.reduce((a, s) => a + pick(s), 0) / m.length) : -1;
              };
              const avgCpu = avgOf((s) => s.cpuUsage);
              const avgMem = avgOf((s) => s.memUsage);
              return (
                <tr key={c.id} className="row-link" onClick={() => onNav({ page: 'client-detail', id: c.id })}>
                  <td>
                    <div className="row">
                      <Avatar name={c.name} size={26}/>
                      <div>
                        <div className="row" style={{ gap: 6 }}>
                          <span style={{ fontWeight: 500 }}>{c.name}</span>
                          {c.ownerTokenId && (
                            <span className="badge" style={{ fontSize: 10, background: 'var(--accent-soft, rgba(99,102,241,.15))' }}
                                  title="Created via the partner API">Partner</span>
                          )}
                        </div>
                        <div className="muted mono" style={{ fontSize: 11 }}>{c.namespace}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className="badge">{c.plan}</span></td>
                  <td>
                    <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                      {c.services.slice(0, 4).map((s) => (
                        <span key={s.name} className="svc-tag" title={`${s.name} · ${s.status}`}>
                          <span className={'status-dot ' + s.status}/>
                          {s.name}
                        </span>
                      ))}
                      {c.services.length > 4 && <span className="muted" style={{ fontSize: 11.5 }}>+{c.services.length - 4}</span>}
                    </div>
                  </td>
                  <td><StatusBadge status={c.status}/></td>
                  <td className="num"><div style={{ width: 80 }}><ResourceBar value={avgCpu}/></div></td>
                  <td className="num"><div style={{ width: 80 }}><ResourceBar value={avgMem}/></div></td>
                  <td className="muted" style={{ fontSize: 12 }}>{c.createdLabel}</td>
                  <td><I.ChevronR size={14} style={{ color: 'var(--text-3)' }}/></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// pollUntil calls `check` every intervalMs until it returns a non-null value
// or `timeoutMs` elapses. Returns the first non-null value or null on
// timeout. Used by the wizard to wait for asynchronous backend / ArgoCD
// state to converge without freezing the UI thread.
async function pollUntil<T>(
  check: () => Promise<T | null>,
  opts: { intervalMs: number; timeoutMs: number },
): Promise<T | null> {
  const deadline = Date.now() + opts.timeoutMs;
  for (;;) {
    try {
      const v = await check();
      if (v !== null) return v;
    } catch {
      // Transient errors don't stop the loop — ArgoCD endpoint may briefly
      // 404 while the App is materializing.
    }
    if (Date.now() >= deadline) return null;
    await new Promise((r) => setTimeout(r, opts.intervalMs));
  }
}

// Build a deep-link into the ArgoCD UI. Base URL priority:
//   1. argoServer from cluster Settings (runtime — admin sets it in the UI,
//      no rebuild needed; the right knob for production)
//   2. VITE_ARGOCD_BASE_URL build-time env (CI override)
//   3. local dev port-forward default
//      (microk8s kubectl -n argocd port-forward svc/argocd-server 8443:443)
function argocdAppURL(appName: string, argoServer?: string): string {
  let base = (argoServer || import.meta.env.VITE_ARGOCD_BASE_URL || 'https://127.0.0.1:8443').trim().replace(/\/$/, '');
  // Tolerate a bare host from Settings (e.g. "argocd.shoptefy.io") — window.open
  // needs an absolute URL, otherwise the browser treats it as a relative path.
  if (base && !/^https?:\/\//i.test(base)) base = 'https://' + base;
  return `${base}/applications/argocd/${encodeURIComponent(appName)}`;
}

// Maps a REAL pod phase/waiting-reason (from the backend) + readiness to the
// visual state shown in the Pods table. The dot value is a StatusDot CSS class.
type PodVisual = { dot: string; textClass: string };
function describePod(phase: string, ready: boolean): PodVisual {
  if (ready) {
    return { dot: 'healthy', textClass: 'muted' };
  }
  switch (phase) {
    case 'Running':
      // Running but not Ready yet (readiness probe failing / warming up).
      return { dot: 'starting', textClass: 'pod-state-pending' };
    case 'ContainerCreating':
    case 'PodInitializing':
      return { dot: 'updating', textClass: 'pod-state-updating' };
    case 'Pending':
      return { dot: 'pending', textClass: 'pod-state-pending' };
    case 'CrashLoopBackOff':
    case 'ImagePullBackOff':
    case 'ErrImagePull':
    case 'CreateContainerError':
    case 'CreateContainerConfigError':
    case 'RunContainerError':
      return { dot: 'failing', textClass: 'pod-state-failing' };
    default:
      // Unknown not-ready reason — treat as still-starting rather than red.
      return { dot: 'starting', textClass: 'pod-state-pending' };
  }
}

// fmtBytes renders a byte count as a human-readable string (MB/GB/TB).
function fmtBytes(b: number): string {
  if (b <= 0) return '0 B';
  if (b >= 1e12) return `${(b / 1e12).toFixed(1)} TB`;
  if (b >= 1 << 30) return `${(b / (1 << 30)).toFixed(1)} GB`;
  return `${(b / (1 << 20)).toFixed(0)} MB`;
}


// podAge renders a compact age (like kubectl's AGE column) from a pod's RFC3339
// startedAt. Returns "—" when the pod has no start time yet (unscheduled).
function podAge(startedAt?: string): string {
  if (!startedAt) return '—';
  const start = Date.parse(startedAt);
  if (Number.isNaN(start)) return '—';
  const secs = Math.max(0, Math.floor((Date.now() - start) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

// ─── ASSET SLOT WIDGET ──────────────────────────────────────────────────────

// AssetSlotWidget renders the upload + version-dropdown + delete controls for
// ONE declared asset slot (e.g. a comfyui workflow). Generic — it knows nothing
// about comfyui; it's driven entirely by the slot declaration on the service.
//
//   - Upload: a version label + a file picker that reads the file's text and
//     POSTs a new immutable version (409 surfaced as "already exists").
//   - Select: a dropdown over the published versions; selecting persists via the
//     service PATCH (assetVersions map) — the same gitSync + rollout other
//     overrides trigger — and materializes the content ConfigMap out-of-band.
//   - Delete: removes the selected version; a 409 ("in use") is surfaced.
function AssetSlotWidget({
  client, svc, slot, setState, toast,
}: {
  client: Client;
  svc: ClientService;
  slot: AssetSlot;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
  toast: ReturnType<typeof useToast>;
}) {
  const selected = svc.assetVersions?.[slot.key] ?? '';
  const [versions, setVersions] = useState<AssetVersion[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadVersion, setUploadVersion] = useState('');
  const [pendingFile, setPendingFile] = useState<{ name: string; content: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const reload = () => {
    listAssetVersions(client.id, svc.name, slot.key)
      .then(setVersions)
      .catch(() => setVersions([]));
  };
  useEffect(reload, [client.id, svc.name, slot.key]);

  // Update the local client's selected version so the dropdown + display stay
  // in sync without a full refetch. An empty version clears this slot's key.
  const patchLocalSelection = (version: string) => {
    setState((s) => ({
      ...s,
      clients: s.clients.map((c) => c.id !== client.id ? c : {
        ...c,
        services: c.services.map((x) => {
          if (x.name !== svc.name) return x;
          const next = { ...(x.assetVersions || {}) };
          if (version === '') delete next[slot.key]; else next[slot.key] = version;
          return { ...x, status: 'restarting' as ClientService['status'], assetVersions: next };
        }),
      }),
    }));
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) { setPendingFile(null); return; }
    const reader = new FileReader();
    reader.onload = () => setPendingFile({ name: f.name, content: String(reader.result ?? '') });
    reader.onerror = () => setErr('Could not read file');
    reader.readAsText(f);
  };

  const onUpload = async () => {
    setErr(null);
    const v = uploadVersion.trim();
    if (!ASSET_VERSION_RE.test(v)) { setErr('Version must match ' + ASSET_VERSION_RE.source); return; }
    if (!pendingFile) { setErr('Pick a file first'); return; }
    if (!pendingFile.content) { setErr('File is empty'); return; }
    setBusy(true);
    try {
      await uploadAssetVersion(client.id, svc.name, slot.key, { version: v, content: pendingFile.content });
      toast?.success('Version published', `${slot.label} ${v}`);
      setUploadVersion('');
      setPendingFile(null);
      if (fileInput.current) fileInput.current.value = '';
      reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onSelect = async (version: string) => {
    setErr(null);
    setBusy(true);
    try {
      // Build the full selection map: clearing drops this slot's key, selecting
      // sets it — preserving any other slot selections this service has.
      const merged = version === ''
        ? Object.fromEntries(Object.entries(svc.assetVersions || {}).filter(([k]) => k !== slot.key))
        : { ...(svc.assetVersions || {}), [slot.key]: version };
      await updateService(client.id, svc.name, { assetVersions: merged });
      patchLocalSelection(version);
      toast?.info?.(`${slot.label} selected`, `${slot.label} → ${version || '(none)'} — rolling out`)
        ?? toast?.success(`${slot.label} selected`, `${slot.label} → ${version || '(none)'}`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (version: string) => {
    setErr(null);
    setBusy(true);
    try {
      await deleteAssetVersion(client.id, svc.name, slot.key, version);
      toast?.success('Version deleted', `${slot.label} ${version}`);
      reload();
    } catch (e) {
      // 409 = the version is selected by a service.
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
        <h4 style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-3)', margin: 0 }}>
          {slot.label} <span style={{ color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>· uploaded, versioned file</span>
        </h4>
        <span className="muted mono" style={{ fontSize: 11 }}>{slot.targetPath}</span>
      </div>

      <div className="res-grid">
        {/* Active version dropdown */}
        <div className="field">
          <label className="field-label">Active version <span className="muted" style={{ fontWeight: 400 }}>· seeded into the pod</span></label>
          <div className="row" style={{ gap: 8 }}>
            <select className="select" value={selected} disabled={busy}
                    onChange={(e) => onSelect(e.target.value)}>
              <option value="">— none —</option>
              {(versions || []).map((v) => (
                <option key={v.version} value={v.version}>{v.version}</option>
              ))}
            </select>
          </div>
          <div className="field-hint">
            {versions === null ? 'Loading versions…'
              : versions.length === 0 ? 'No versions yet — upload one to select it.'
              : `${versions.length} version${versions.length === 1 ? '' : 's'} published. Selecting one rolls the pod.`}
          </div>
          {/* Shared pool: versions are shared across every instance of this
              service across ALL tenants — uploading from one tenant's instance
              publishes to the shared pool the same instance sees everywhere. */}
          <div className="field-hint muted" style={{ marginTop: 2 }}>
            Versions are shared across all <span className="mono">{svc.name}</span> instances (every tenant).
          </div>
        </div>

        {/* Published versions list with per-version delete. The currently
            selected version can't be deleted from here (you can't delete the
            version you're running); the backend additionally returns 409 if any
            OTHER tenant's instance selects it (shared pool, in-use guard). */}
        <div className="field">
          <label className="field-label">Published versions <span className="muted" style={{ fontWeight: 400 }}>· shared pool</span></label>
          {versions === null ? (
            <div className="field-hint">Loading versions…</div>
          ) : versions.length === 0 ? (
            <div className="field-hint">No versions yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {versions.map((v) => {
                const isSelected = selected === v.version;
                return (
                  <div key={v.version} className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
                    <span className="mono" style={{ fontSize: 12 }}>
                      {v.version}{isSelected && <span className="muted" style={{ marginLeft: 6 }}>· active</span>}
                    </span>
                    <button className="btn btn-sm" disabled={busy || isSelected}
                            title={isSelected
                              ? 'Cannot delete the active version — switch the selection first'
                              : 'Delete this version (must not be in use by any tenant)'}
                            onClick={() => onDelete(v.version)}><I.Trash size={12}/></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Upload a new version */}
        <div className="field">
          <label className="field-label">Publish new version <span className="muted" style={{ fontWeight: 400 }}>· immutable</span></label>
          <div className="row" style={{ gap: 8 }}>
            <input className="input input-mono" type="text" placeholder="e.g. 1.0.0" style={{ flex: 'none', width: 120 }}
                   value={uploadVersion} disabled={busy}
                   onChange={(e) => setUploadVersion(e.target.value.trim())}/>
            <input ref={fileInput} type="file" accept={slot.accept} disabled={busy}
                   onChange={onPickFile} style={{ fontSize: 12, flex: 1 }}/>
            <button className="btn btn-sm btn-primary" disabled={busy || !pendingFile || !uploadVersion.trim()}
                    onClick={onUpload}><I.Plus size={12}/> Publish</button>
          </div>
          <div className="field-hint">
            {pendingFile ? `${pendingFile.name} ready (${pendingFile.content.length} bytes)`
              : `Pick a ${slot.accept || 'file'} and give it a version, then Publish.`}
          </div>
        </div>
      </div>
      {err && <div className="field-error" style={{ marginTop: 6 }}>{err}</div>}
    </div>
  );
}

// ─── CLIENT DETAIL ──────────────────────────────────────────────────────────
export function ClientDetail({
  client, state, setState, onNav, route,
}: {
  client: Client;
  state: AppState;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
  onNav: (r: Route) => void;
  route: Route & { tab?: string; service?: string };
}) {
  const toast = useToast();
  const { unlocked: operator } = useOperatorMode();
  const [tab, setTab] = useState<string>((route as any).tab || 'services');
  const [activeService, setActiveService] = useState<string>((route as any).service || client.services[0]?.name || '');

  // Plan-tier change: PATCH the tenant, which re-renders the namespace
  // ResourceQuota in git → ArgoCD applies the new caps. Downgrades are "soft"
  // (existing pods/PVCs stay; new ones blocked until usage is under the cap),
  // so we warn before lowering.
  const planRank: Record<string, number> = { starter: 0, growth: 1, scale: 2, enterprise: 3, unlimited: 4 };
  const [planOpen, setPlanOpen] = useState(false);
  const [planDraft, setPlanDraft] = useState<Plan>(client.plan);
  const [planBusy, setPlanBusy] = useState(false);
  const openPlan = () => { setPlanDraft(client.plan); setPlanOpen(true); };

  // Ingress modal — per-service domain + TLS toggle
  const [ingressOpen, setIngressOpen] = useState(false);
  const [ingressSvc, setIngressSvc] = useState<string>('');
  const [ingressDraft, setIngressDraft] = useState('');
  const [ingressTls, setIngressTls] = useState(true);
  const [ingressBusy, setIngressBusy] = useState(false);
  const openIngress = (svcName: string, current: string, tls: boolean) => {
    setIngressSvc(svcName); setIngressDraft(current); setIngressTls(tls); setIngressOpen(true);
  };
  const saveIngress = async () => {
    setIngressBusy(true);
    try {
      const updated = await setServiceIngress(client.id, ingressSvc, ingressDraft.trim(), ingressTls);
      setState((s) => ({
        ...s,
        clients: s.clients.map((c) => c.id !== client.id ? c : {
          ...c, services: c.services.map((x) => x.name === ingressSvc ? updated : x),
        }),
      }));
      setIngressOpen(false);
      toast?.success('Ingress saved', ingressDraft.trim());
    } catch (e) {
      toast?.error('Save failed', e instanceof ApiError ? e.message : String(e));
    } finally { setIngressBusy(false); }
  };
  const removeIngress = async () => {
    setIngressBusy(true);
    try {
      const updated = await clearServiceIngress(client.id, ingressSvc);
      setState((s) => ({
        ...s,
        clients: s.clients.map((c) => c.id !== client.id ? c : {
          ...c, services: c.services.map((x) => x.name === ingressSvc ? updated : x),
        }),
      }));
      setIngressOpen(false);
      toast?.success('Ingress removed');
    } catch (e) {
      toast?.error('Remove failed', e instanceof ApiError ? e.message : String(e));
    } finally { setIngressBusy(false); }
  };

  const isDowngrade = (planRank[planDraft] ?? 0) < (planRank[client.plan] ?? 0);
  const submitPlan = async () => {
    if (planDraft === client.plan) { setPlanOpen(false); return; }
    setPlanBusy(true);
    try {
      const updated = await updateTenant(client.id, { plan: planDraft });
      setState((s) => ({
        ...s,
        clients: s.clients.map((c) => c.id === client.id ? { ...c, plan: updated.plan } : c),
      }));
      setPlanOpen(false);
      toast?.success('Plan changed', `${client.id} is now ${updated.plan}`);
    } catch (e) {
      toast?.error('Plan change failed', e instanceof ApiError ? e.message : String(e));
    } finally {
      setPlanBusy(false);
    }
  };

  // Real cluster nodes for the Edit-resources node picker. Replaces the old
  // hardcoded mock list (data/mock.ts NODES) so the picker offers the actual
  // schedulable nodes the backend knows about.
  const [clusterNodes, setClusterNodes] = useState<NodeStatus[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchNodes()
      .then((ns) => { if (!cancelled) setClusterNodes(ns); })
      .catch(() => { if (!cancelled) setClusterNodes([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const r = route as any;
    if (r.tab) setTab(r.tab);
    if (r.service) setActiveService(r.service);
  }, [(route as any).tab, (route as any).service]);

  // Timestamp of the last Restart click — controls optimistic "restarting"
  // override and accelerates polling.
  const [restartClickedAt, setRestartClickedAt] = useState<number | null>(null);

  // Per-service "BE has already confirmed the rollout" set, keyed by service
  // name. Reset every time the user triggers a new rollout (restartClickedAt
  // changes). Once BE reports a non-healthy status for a service, we stop
  // overriding — the next healthy report means the rollout actually finished.
  const beAckedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (restartClickedAt !== null) beAckedRef.current = new Set();
  }, [restartClickedAt]);

  // Live polling: while the Services tab is open, refresh this tenant so
  // CPU/mem usage + rollout state stay current. Cadence:
  //   - default:               10 seconds
  //   - within 60s of a rollout trigger: 3 seconds
  //   - if any service is already "restarting": 3 seconds
  useEffect(() => {
    if (tab !== 'services') return;
    let cancelled = false;
    const tick = async () => {
      try {
        const fresh = await fetchTenant(client.id);
        if (cancelled) return;
        setState((s) => ({
          ...s,
          clients: s.clients.map((c) => {
            if (c.id !== fresh.id) return c;
            const within = restartClickedAt && (Date.now() - restartClickedAt) < 60_000;
            return {
              ...fresh,
              services: fresh.services.map((freshSvc) => {
                const localSvc = c.services.find((x) => x.name === freshSvc.name);
                if (!localSvc) return freshSvc;
                // BE reports something other than healthy → trust it AND
                // remember that BE has now acknowledged the rollout. From
                // the next healthy report onward we let it through.
                if (freshSvc.status !== 'healthy') {
                  beAckedRef.current.add(freshSvc.name);
                  return freshSvc;
                }
                // BE says healthy. Hold the optimistic "restarting" only
                // when we genuinely have no signal yet (BE has not seen
                // the rollout) AND we're still in the rollout window AND
                // we did flag this service locally as restarting.
                const localRestarting = (localSvc.status as string) === 'restarting';
                const beAcked = beAckedRef.current.has(freshSvc.name);
                if (localRestarting && within && !beAcked) {
                  return { ...freshSvc, status: 'restarting' as any };
                }
                return freshSvc;
              }),
            };
          }),
        }));
      } catch { /* network blip — try again next tick */ }
    };
    const restartWindowOpen = restartClickedAt && (Date.now() - restartClickedAt) < 60_000;
    const anyRestarting = client.services.some((s) => (s.status as string) === 'restarting');
    const intervalMs = (restartWindowOpen || anyRestarting) ? 3_000 : 10_000;
    const id = setInterval(tick, intervalMs);
    const off = onVisible(tick);
    return () => { cancelled = true; clearInterval(id); off(); };
  }, [tab, client.id, setState, restartClickedAt, client.services]);

  // Fall back to a blank placeholder when the tenant has no services yet: the
  // logs/edit modals and config tab reference `svc` during render regardless of
  // the active tab, so it must never be undefined. The visible services + config
  // tabs are gated on services.length below, so this placeholder is never shown.
  const svc = client.services.find((s) => s.name === activeService) || client.services[0] || ({
    name: '', icon: '', group: 'web', status: 'unknown', replicas: 0, readyReplicas: 0,
    cpuLimit: '', memLimit: '', nodeName: '', cpuUsage: -1, memUsage: -1,
    diskBytes: 0, diskCapacityBytes: 0, gpuCount: 0, gpuUtilPercent: -1,
    gpuMemUsedBytes: 0, gpuMemCapacityBytes: 0, image: '', uptime: '',
  } as unknown as (typeof client.services)[number]);

  const [edit, setEdit] = useState<EditState>(null);
  const startEdit = () => setEdit({
    replicas: svc.replicas,
    cpuLimit: svc.cpuLimit,
    memLimit: svc.memLimit,
    nodeName: svc.nodeName,
    imageTag: svc.imageTag ?? '',
  });
  const cancelEdit = () => setEdit(null);

  // Image-tag listing: when the edit panel opens we ask the BE whether this
  // service's image is listable. If so (supported + tags), the Image version
  // field becomes an editable combobox (datalist) over the known tags; otherwise
  // it stays a plain free-text input. The endpoint never errors hard (it
  // degrades to {supported:false}), so a network failure simply keeps the text
  // input. Re-fetched whenever the panel opens for a different service.
  const [imageTags, setImageTags] = useState<ImageTags | null>(null);
  const editing = edit !== null;
  useEffect(() => {
    if (!editing) { setImageTags(null); return; }
    let cancelled = false;
    setImageTags(null);
    fetchServiceImageTags(client.id, activeService)
      .then((res) => { if (!cancelled) setImageTags(res); })
      .catch(() => { if (!cancelled) setImageTags({ supported: false }); });
    return () => { cancelled = true; };
  }, [editing, client.id, activeService]);
  const tagListId = `image-tags-${activeService}`;
  const hasTagOptions = !!imageTags?.supported && (imageTags.tags?.length ?? 0) > 0;

  // The "unlimited" plan lifts the per-service caps (the backend skips its
  // plan-tier check), so the edit sliders shouldn't box the operator in either.
  const unlimitedPlan = client.plan === 'unlimited';
  const CPU_MIN = 100, CPU_MAX = unlimitedPlan ? 128000 : svc.group === 'gpu' ? 8000 : 4000;
  const MEM_MIN_GI = 0.25, MEM_MAX_GI = unlimitedPlan ? 1024 : svc.group === 'gpu' ? 32 : 8;
  const REPLICA_MIN = 0, REPLICA_MAX = unlimitedPlan ? 100 : svc.group === 'gpu' ? 6 : 12;

  const parseCpu = (s: string) => parseInt(s.replace(/m$/, '')) || 0;
  const parseMemGi = (s: string) => {
    const m = s.match(/(\d+(?:\.\d+)?)(Gi|Mi)/);
    if (!m) return 0;
    return m[2] === 'Gi' ? parseFloat(m[1]) : parseFloat(m[1]) / 1024;
  };
  // Prefill defaults are clamped up to the edit-form minimums, so a freshly
  // added service (e.g. redis/nginx charts default to 128Mi) isn't already
  // below the limit the Edit-resources form enforces (256Mi).
  const defaultCpuStr = (raw: string) => `${Math.max(parseCpu(raw), CPU_MIN)}m`;
  const defaultMemStr = (raw: string) => `${Math.max(parseMemGi(raw), MEM_MIN_GI)}Gi`;

  const editCpu = edit ? parseCpu(edit.cpuLimit) : 0;
  const editMem = edit ? parseMemGi(edit.memLimit) : 0;
  const errors: Record<string, string | null> = edit ? {
    replicas: (Number.isNaN(edit.replicas) || edit.replicas < REPLICA_MIN || edit.replicas > REPLICA_MAX) ? `must be ${REPLICA_MIN}–${REPLICA_MAX}` : null,
    cpu: (editCpu < CPU_MIN || editCpu > CPU_MAX) ? `must be ${CPU_MIN}m–${CPU_MAX}m` : null,
    mem: (editMem < MEM_MIN_GI || editMem > MEM_MAX_GI) ? `must be ${MEM_MIN_GI}Gi–${MEM_MAX_GI}Gi` : null,
    imageTag: (edit.imageTag !== '' && !IMAGE_TAG_RE.test(edit.imageTag)) ? 'invalid image tag' : null,
  } : {};
  const hasErrors = Object.values(errors).some(Boolean);
  const isDirty = !!edit && (
    edit.replicas !== svc.replicas ||
    edit.cpuLimit !== svc.cpuLimit ||
    edit.memLimit !== svc.memLimit ||
    edit.nodeName !== svc.nodeName ||
    edit.imageTag !== (svc.imageTag ?? '')
  );

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [confirmDelSvc, setConfirmDelSvc] = useState(false);
  const [deletingSvc, setDeletingSvc] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsText, setLogsText] = useState<string>('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsErr, setLogsErr] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);
  const [addForm, setAddForm] = useState({ service: 'grafana', replicas: 1, cpuLimit: '300m', memLimit: '256Mi' });
  // Live chart catalog so the "Add service" picker offers exactly what the
  // backend can provision (helmCatalog) — any chart added there appears here
  // automatically, no hardcoded list.
  const [chartCatalog, setChartCatalog] = useState<ChartCatalogEntry[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchChartCatalog()
      .then((c) => { if (!cancelled) setChartCatalog(c); })
      .catch(() => { if (!cancelled) setChartCatalog([]); });
    return () => { cancelled = true; };
  }, []);

  // A tenant can't have two services with the same name (DB unique on
  // tenant+name → a duplicate add fails), so the picker offers only charts the
  // client doesn't already have.
  const availableCharts = useMemo(() => {
    const taken = new Set(client.services.map((s) => s.name));
    return chartCatalog.filter((c) => !taken.has(c.name));
  }, [chartCatalog, client.services]);

  // When the Add-service dialog opens, default the form to the first catalog
  // entry and pre-fill ITS catalog default resources. Without this the form
  // kept a hardcoded 300m/256Mi for every service — too low for GPU charts and,
  // when below the chart's resource request, it produced an invalid Deployment
  // (request > limit). The per-service onChange below keeps them in sync too.
  //
  // Initialise ONLY once per open: `availableCharts` is memoised on
  // client.services, which the services-tab poll re-references every few
  // seconds. Without the ref guard, each poll re-ran this effect and clobbered
  // the operator's replica/CPU/mem edits back to defaults mid-typing.
  const addFormInitedRef = useRef(false);
  useEffect(() => {
    if (!addOpen) { addFormInitedRef.current = false; return; }
    if (addFormInitedRef.current || availableCharts.length === 0) return;
    addFormInitedRef.current = true;
    const def = availableCharts[0];
    setAddForm({
      service: def.name,
      replicas: def.defaultReplicas || 1,
      cpuLimit: defaultCpuStr(def.defaultCpu),
      memLimit: defaultMemStr(def.defaultMem),
    });
  }, [addOpen, availableCharts]);

  const submitAddService = async () => {
    setAddBusy(true);
    setAddErr(null);
    try {
      const replicas = Number.isFinite(addForm.replicas) && addForm.replicas > 0 ? addForm.replicas : 1;
      const created = await addService(client.id, { ...addForm, replicas });
      setState((s) => ({
        ...s,
        clients: s.clients.map((c) => c.id !== client.id ? c : {
          ...c, services: [...c.services, created],
        }),
      }));
      setAddOpen(false);
      setActiveService(created.name);
      toast?.success('Service added', `${created.name} is being provisioned`);
    } catch (e) {
      setAddErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setAddBusy(false);
    }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    setLogsErr(null);
    try {
      const t = await fetchServiceLogs(client.id, svc.name, 300);
      setLogsText(t || '(no log output)');
    } catch (e) {
      setLogsErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLogsLoading(false);
    }
  };
  const openLogs = () => { setLogsOpen(true); loadLogs(); };

  const handleRestart = async () => {
    setRestarting(true);
    let updated: ClientService;
    try {
      updated = await restartService(client.id, svc.name);
    } catch (err) {
      setRestarting(false);
      toast?.error('Restart failed', err instanceof ApiError ? err.message : String(err));
      return;
    }
    // Optimistically mark the service as "restarting" — BE eventually
    // reports the same once kubectl's view catches up to the rollout, but
    // there's a ~5-15s window where it still says "healthy" because the
    // old pod is still serving traffic. We override that locally.
    const optimistic: ClientService = { ...updated, status: 'restarting' as any };
    setRestartClickedAt(Date.now());
    setState((s) => ({
      ...s,
      clients: s.clients.map((c) => c.id !== client.id ? c : {
        ...c,
        services: c.services.map((x) => x.name === svc.name ? optimistic : x),
      }),
    }));
    setRestarting(false);
    toast?.info?.('Restart triggered', `New ${svc.name} pod is rolling out`)
      ?? toast?.success('Restart triggered', `New ${svc.name} pod is rolling out`);
  };

  const handleDeleteService = async () => {
    const removed = svc.name;
    setDeletingSvc(true);
    try {
      await deleteService(client.id, removed);
    } catch (err) {
      setDeletingSvc(false);
      toast?.error('Delete failed', err instanceof ApiError ? err.message : String(err));
      return;
    }
    setDeletingSvc(false);
    setConfirmDelSvc(false);
    setEdit(null);
    // Drop it from local state and move to the first remaining service.
    setState((s) => ({
      ...s,
      clients: s.clients.map((c) => c.id !== client.id ? c : {
        ...c, services: c.services.filter((x) => x.name !== removed),
      }),
    }));
    setActiveService(client.services.filter((x) => x.name !== removed)[0]?.name ?? '');
    toast?.success('Service removed', `${removed} deleted — ArgoCD will prune the workload`);
  };

  const applyChanges = async () => {
    if (!edit) return;
    setSaving(true);

    // Build a sparse patch — only fields that actually changed.
    const patch: Parameters<typeof updateService>[2] = {};
    if (edit.replicas !== svc.replicas) patch.replicas = edit.replicas;
    if (edit.cpuLimit !== svc.cpuLimit) patch.cpuLimit = edit.cpuLimit;
    if (edit.memLimit !== svc.memLimit) patch.memLimit = edit.memLimit;
    if (edit.nodeName !== svc.nodeName) patch.nodeName = edit.nodeName;
    // An explicit empty string clears the override (revert to chart default).
    if (edit.imageTag !== (svc.imageTag ?? '')) patch.imageTag = edit.imageTag;

    let updated: ClientService;
    try {
      updated = await updateService(client.id, svc.name, patch);
    } catch (err) {
      setSaving(false);
      const msg = err instanceof ApiError ? err.message : String(err);
      toast?.error('Update failed', msg);
      return;
    }

    // Changes that propagate to Helm values cause ArgoCD to roll the pod.
    // Mark the local service "restarting" optimistically so the status
    // dot doesn't stay green until kubectl sees the change.
    const willRollout =
      patch.replicas !== undefined ||
      patch.cpuLimit !== undefined ||
      patch.memLimit !== undefined ||
      patch.imageTag !== undefined;
    const optimistic: ClientService = willRollout
      ? { ...updated, status: 'restarting' as any }
      : updated;
    if (willRollout) setRestartClickedAt(Date.now());

    setState((s) => {
      const clients = s.clients.map((c) => {
        if (c.id !== client.id) return c;
        return {
          ...c,
          services: c.services.map((x) => x.name === svc.name ? optimistic : x),
        };
      });
      return { ...s, clients };
    });
    setEdit(null);
    setConfirmOpen(false);
    setSaving(false);
    if (willRollout) {
      toast?.info?.('Changes saved', `${svc.name} is rolling out`)
        ?? toast?.success('Changes saved', `${svc.name} is rolling out`);
    } else {
      toast?.success('Changes saved', `${svc.name} updated`);
    }
  };

  return (
    <div className="page-narrow">
      <div className="page-head">
        <div className="row" style={{ gap: 14 }}>
          <button className="btn btn-ghost btn-icon btn-sm" aria-label="Back to clients"
                  title="Back to clients" onClick={() => onNav({ page: 'clients' })}>
            <I.ChevronL size={15}/>
          </button>
          <Avatar name={client.name} size={36}/>
          <div style={{ flex: 1 }}>
            <h1 className="page-title" style={{ marginBottom: 2 }}>{client.name}</h1>
            <div className="row muted" style={{ fontSize: 12.5, gap: 8 }}>
              <span className="mono">{client.namespace}</span>
              <span className="dot-sep">·</span>
              <span className="row" style={{ gap: 4 }}><I.GitBranch size={12}/> <span className="mono">{client.repo.replace(/^.+\//, '')}</span></span>
              <span className="dot-sep">·</span>
              <span>created {client.createdLabel}</span>
            </div>
          </div>
          <StatusBadge status={client.status}/>
          {operator && (
            <button className="btn" onClick={() => window.open(client.repo, '_blank', 'noopener')}>
              <I.Git size={13}/> Open repo <I.ExtLink size={11}/>
            </button>
          )}
        </div>
      </div>

      <Modal open={planOpen} onClose={() => { if (!planBusy) setPlanOpen(false); }}
             title={`Change plan for "${client.name}"`}
             subtitle="The namespace ResourceQuota is re-rendered into git and ArgoCD applies the new caps within ~30s."
             dismissable={!planBusy}
             footer={
               <>
                 <button className="btn" disabled={planBusy} onClick={() => setPlanOpen(false)}>Cancel</button>
                 <button className={'btn ' + (isDowngrade ? 'btn-danger-solid' : 'btn-primary')}
                         disabled={planBusy || planDraft === client.plan}
                         onClick={submitPlan}>
                   {planBusy ? 'Applying…' : isDowngrade ? 'Downgrade plan' : 'Change plan'}
                 </button>
               </>
             }>
        <div className="field">
          <label className="field-label">Plan tier</label>
          <select className="select" value={planDraft} disabled={planBusy}
                  onChange={(e) => setPlanDraft(e.target.value as Plan)}>
            {PLANS.map((p) => (
              <option key={p} value={p}>{p}{p === client.plan ? ' (current)' : ''}</option>
            ))}
          </select>
          <div className="field-hint">
            Sized for the 4× comfyui + eq baseline: every tier holds a full client; higher tiers add headroom (replicas, more services, output growth).
          </div>
        </div>
        {isDowngrade && (
          <div className="field-error" style={{ fontSize: 12.5, marginTop: 4 }}>
            Downgrade is soft: running pods and PVCs stay, but new ones are blocked until usage is under the lower cap. PVCs are never shrunk.
          </div>
        )}
      </Modal>


      <Modal open={ingressOpen} onClose={() => { if (!ingressBusy) setIngressOpen(false); }}
             title={ingressSvc && client.services.find((x) => x.name === ingressSvc)?.ingressHost ? 'Edit domain' : 'Set domain'}
             subtitle={`Ingress for service ${ingressSvc} — ArgoCD applies the change in ~30s.`}
             dismissable={!ingressBusy}
             footer={
               <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                 <div>
                   {ingressSvc && client.services.find((x) => x.name === ingressSvc)?.ingressHost && (
                     <button className="btn btn-danger btn-sm" disabled={ingressBusy} onClick={removeIngress}>
                       Remove domain
                     </button>
                   )}
                 </div>
                 <div className="row" style={{ gap: 8 }}>
                   <button className="btn" disabled={ingressBusy} onClick={() => setIngressOpen(false)}>Cancel</button>
                   <button className="btn btn-primary" disabled={ingressBusy || !ingressDraft.trim()} onClick={saveIngress}>
                     {ingressBusy ? 'Saving…' : 'Save'}
                   </button>
                 </div>
               </div>
             }>
        <div className="field">
          <label className="field-label">Hostname</label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
                           fontSize: 12.5, color: 'var(--text-3)', pointerEvents: 'none', userSelect: 'none' }}>
              https://
            </span>
            <input className="input" placeholder="app.shoptefy.io"
                   style={{ paddingLeft: 64 }}
                   value={ingressDraft}
                   onChange={(e) => setIngressDraft(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter' && ingressDraft.trim()) saveIngress(); }}
                   autoFocus/>
          </div>
          <div className="field-hint">Hostname only, without protocol — e.g. <span className="mono">client.shoptefy.io</span></div>
        </div>

        <div className="field" style={{ marginTop: 4 }}>
          <label className="field-label">TLS certificate</label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                          padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)',
                          background: ingressTls ? 'color-mix(in srgb, var(--accent) 6%, transparent)' : 'transparent',
                          transition: 'background 0.15s' }}>
            <input type="checkbox" checked={ingressTls} style={{ marginTop: 2, accentColor: 'var(--accent)' }}
                   onChange={(e) => setIngressTls(e.target.checked)}/>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Automatic TLS (cert-manager)</div>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                cert-manager automatically issues a TLS certificate for the domain.
              </div>
            </div>
          </label>
        </div>

        {ingressDraft.trim() && (
          <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8,
                        background: 'var(--bg-3)', border: '1px solid var(--border)' }}>
            <div className="muted" style={{ fontSize: 11, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Resulting URL</div>
            <a href={`${ingressTls ? 'https' : 'http'}://${ingressDraft.trim()}`}
               target="_blank" rel="noopener"
               style={{ fontSize: 13, color: 'var(--accent)', wordBreak: 'break-all', fontFamily: 'monospace' }}>
              {ingressTls ? 'https' : 'http'}://{ingressDraft.trim()}
            </a>
          </div>
        )}
      </Modal>

      <div className="tabs">
        {['overview', 'services', 'config', 's3', 'tokens', 'activity', 'gitops', 'danger'].map((t) => (
          <button key={t} className={'tab ' + (tab === t ? 'active' : '')}
                  onClick={() => setTab(t)}>
            {t === 'overview' ? 'Overview'
              : t === 'services' ? `Services (${client.services.length})`
              : t === 'config' ? 'Live config'
              : t === 's3' ? 'S3'
              : t === 'tokens' ? 'API tokens'
              : t === 'activity' ? 'Activity'
              : t === 'gitops' ? 'GitOps history'
              : 'Danger zone'}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <ClientOverviewTab client={client} setState={setState} argoServer={state.settings.argoServer} onChangePlan={openPlan} onNav={(r) => { setTab(r.tab || 'services'); if (r.service) setActiveService(r.service); }}/>
      )}

      {tab === 'services' && client.services.length === 0 && (
        <div className="card card-pad" style={{ textAlign: 'center', padding: '44px 20px' }}>
          <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>This tenant has no services yet.</div>
          <button className="btn btn-primary"
                  disabled={chartCatalog.length > 0 && availableCharts.length === 0}
                  onClick={() => setAddOpen(true)}>
            <I.Plus size={14}/> Add service
          </button>
        </div>
      )}
      {tab === 'services' && client.services.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 14 }}>
          <div className="card" style={{ padding: 6, alignSelf: 'start' }}>
            {client.services.map((s) => {
              const live = (s.status as string) === 'restarting' || (s.status as string) === 'pending';
              return (
                <button key={s.name}
                        className={'nav-item' + (activeService === s.name ? ' active' : '')}
                        title={`${s.name} — ${s.status}`}
                        onClick={() => { setActiveService(s.name); setEdit(null); }}
                        style={{ padding: '9px 11px' }}>
                  <StatusDot status={s.status}/>
                  <span className="mono" style={{ fontSize: 12.5 }}>{s.name}</span>
                  {live && (
                    <span className="muted" style={{ fontSize: 10.5, fontStyle: 'italic', marginLeft: 4 }}>
                      {s.status === 'restarting' ? 'rolling…' : 'pending…'}
                    </span>
                  )}
                  <span className="nav-badge">{s.readyReplicas}/{s.replicas}</span>
                </button>
              );
            })}
            {(() => {
              const full = chartCatalog.length > 0 && availableCharts.length === 0;
              return (
                <button className="nav-item" disabled={full}
                        style={{ padding: '9px 11px', color: full ? 'var(--text-3)' : 'var(--accent)',
                                 opacity: full ? 0.6 : 1, cursor: full ? 'not-allowed' : 'pointer' }}
                        title={full ? 'This client already has every service in the catalog' : undefined}
                        onClick={() => setAddOpen(true)}>
                  <I.Plus size={14}/>
                  <span style={{ fontSize: 12.5 }}>{full ? 'All services added' : 'Add service'}</span>
                </button>
              );
            })()}
          </div>

          <div className="card">
            <div className="card-head">
              <div className="row" style={{ gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 6, background: 'var(--bg-3)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ServiceIcon name={svc.icon} size={16}/>
                </div>
                <div>
                  <h3 style={{ fontSize: 14 }}>{svc.name}</h3>
                  <div className="muted mono" style={{ fontSize: 11.5 }}>{svc.image}</div>
                </div>
                <StatusBadge status={svc.status}/>
              </div>
              <div className="row" style={{ gap: 6 }}>
                <button className="btn btn-sm" onClick={handleRestart} disabled={restarting}>
                  <I.Restart size={12}/> {restarting ? 'Restarting…' : 'Restart'}
                </button>
                <button className="btn btn-sm" onClick={openLogs}><I.Terminal size={12}/> Logs</button>
                {!edit && <button className="btn btn-sm btn-danger" title="Remove this service"
                                  onClick={() => setConfirmDelSvc(true)}><I.Trash size={12}/> Delete</button>}
                {!edit && <button className="btn btn-sm btn-primary" onClick={startEdit}>Edit resources</button>}
              </div>
            </div>
            <div className="card-pad">
              {!edit && (
                <>
                  {/* Metadata — short, single-line fields */}
                  <div className="res-grid">
                    <KV label="Replicas" value={
                      <span>
                        <span style={{ fontSize: 18, fontWeight: 600 }}>{svc.replicas}</span>
                        <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>· ready {svc.readyReplicas}/{svc.replicas}</span>
                      </span>
                    }/>
                    <KV label="Node" value={<span className="mono">{svc.nodeName}</span>}/>
                    <KV label="Image version" value={<span className="mono">{svc.imageTag || 'chart default'}</span>}/>
                    <KV label="Uptime" value={<span className="mono">{svc.uptime}</span>}/>
                    <KV label="Image" value={<span className="mono" style={{ fontSize: 11.5, wordBreak: 'break-all' }}>{svc.image}</span>}/>
                    <KV label="Ingress" value={
                      svc.ingressHost ? (
                        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                          <a href={`https://${svc.ingressHost}`} target="_blank" rel="noopener"
                             className="mono" style={{ fontSize: 12, color: 'var(--accent)' }}>
                            <I.ExtLink size={11}/> {svc.ingressHost}
                            {svc.tlsEnabled && <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>· TLS</span>}
                          </a>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', fontSize: 12 }}
                                  onClick={() => openIngress(svc.name, svc.ingressHost!, svc.tlsEnabled ?? true)}>
                            Edit
                          </button>
                        </div>
                      ) : (
                        <button className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', fontSize: 12 }}
                                onClick={() => openIngress(svc.name, '', true)}>
                          <I.Plus size={11}/> Set domain
                        </button>
                      )
                    }/>
                  </div>

                  {/* Resource usage — uniform tiles (value + bar + caption) that
                      line up on one row. CPU / Memory / Disk / GPU. */}
                  <div className="divider"></div>
                  <div className="usage-grid">
                    <UsageCell label="CPU limit" head={svc.cpuLimit} pct={svc.cpuUsage}
                               sub={svc.cpuUsage >= 0 ? `using ~${svc.cpuUsage}% of limit` : 'usage n/a (no metrics source)'}/>
                    <UsageCell label="Memory limit" head={svc.memLimit} pct={svc.memUsage}
                               sub={svc.memUsage >= 0 ? `using ~${svc.memUsage}% of limit` : 'usage n/a (no metrics source)'}/>
                    <UsageCell label="GPU"
                               head={svc.gpuCount > 0
                                 ? `${svc.gpuCount} GPU${svc.gpuCount === 1 ? '' : 's'} · ${svc.gpuUtilPercent >= 0 ? `~${svc.gpuUtilPercent}% util` : 'util n/a'}`
                                 : '—'}
                               pct={svc.gpuCount > 0 ? svc.gpuUtilPercent : -1}
                               sub={svc.gpuCount > 0
                                 ? (svc.gpuMemCapacityBytes > 0 ? `${fmtBytes(svc.gpuMemUsedBytes)} / ${fmtBytes(svc.gpuMemCapacityBytes)} VRAM` : 'VRAM n/a')
                                 : 'usage n/a (no GPU / metrics source)'}/>
                  </div>

                  <div className="divider"></div>
                  <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
                    <h4 style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-3)', margin: 0 }}>
                      Pods <span style={{ color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>{svc.readyReplicas}/{svc.replicas} ready</span>
                    </h4>
                    {svc.status === 'restarting' && (
                      <span className="muted" style={{ fontSize: 11.5 }}>
                        Rolling update in progress — {svc.replicas - svc.readyReplicas} pod{svc.replicas - svc.readyReplicas === 1 ? '' : 's'} updating
                      </span>
                    )}
                  </div>
                  {svc.pods && svc.pods.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px 130px 90px', gap: 8, fontSize: 12.5, alignItems: 'center' }}>
                      {svc.pods.map((p) => {
                        const pod = describePod(p.phase, p.ready);
                        return (
                          <Fragment key={p.name}>
                            <div className="row" style={{ gap: 8 }}>
                              <StatusDot status={pod.dot}/>
                              <span className="mono" style={{ fontSize: 12 }}>{p.name}</span>
                            </div>
                            <span className="mono muted" style={{ fontSize: 11.5 }}>{p.node || '—'}</span>
                            <span className={pod.textClass} style={{ fontSize: 12 }}>{p.ready ? 'Running' : p.phase}</span>
                            <span className="muted mono" style={{ fontSize: 11.5 }}>{podAge(p.startedAt)}</span>
                          </Fragment>
                        );
                      })}
                    </div>
                  ) : (
                    // No real per-pod data from the backend (noop backend, or
                    // none scheduled yet) — show the aggregate count instead of
                    // fabricating pod names.
                    <div className="muted" style={{ fontSize: 12.5 }}>
                      {svc.readyReplicas}/{svc.replicas} pod{svc.replicas === 1 ? '' : 's'} ready
                      <span style={{ marginLeft: 6 }}>· per-pod detail unavailable</span>
                    </div>
                  )}
                </>
              )}

              {edit && (
                <div>
                  <div className="row" style={{ marginBottom: 14, gap: 10 }}>
                    <I.Info size={14} style={{ color: 'var(--accent)' }}/>
                    <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                      Changes are written to <span className="mono" style={{ color: 'var(--text-1)' }}>{client.repo.replace(/.+\//, '')}</span> and applied by ArgoCD within 30 seconds.
                    </span>
                  </div>
                  <div className="res-grid">
                    <div className="field">
                      <label className="field-label">Replicas <span className="muted" style={{ fontWeight: 400 }}>· {REPLICA_MIN}–{REPLICA_MAX}</span></label>
                      <div className="row" style={{ gap: 8 }}>
                        <button className="btn btn-icon btn-sm" disabled={edit.replicas <= REPLICA_MIN}
                                onClick={() => setEdit({ ...edit, replicas: Math.max(REPLICA_MIN, edit.replicas - 1) })}><I.Minus size={12}/></button>
                        <input className={'input ' + (errors.replicas ? 'input-error' : '')}
                               style={{ textAlign: 'center', width: 70, flex: 'none' }}
                               type="number" min={REPLICA_MIN} max={REPLICA_MAX}
                               value={Number.isNaN(edit.replicas) ? '' : edit.replicas}
                               onChange={(e) => setEdit({ ...edit, replicas: e.target.value === '' ? NaN : parseInt(e.target.value, 10) })}/>
                        <button className="btn btn-icon btn-sm" disabled={edit.replicas >= REPLICA_MAX}
                                onClick={() => setEdit({ ...edit, replicas: Math.min(REPLICA_MAX, edit.replicas + 1) })}><I.Plus size={12}/></button>
                        <span className="muted" style={{ fontSize: 11.5, marginLeft: 4 }}>currently {svc.replicas}</span>
                      </div>
                      {errors.replicas && <div className="field-error">{errors.replicas}</div>}
                    </div>
                    <div className="field">
                      <label className="field-label">Node <span className="muted" style={{ fontWeight: 400 }}>· scheduler will respect this</span></label>
                      <select className="select" value={edit.nodeName} onChange={(e) => setEdit({ ...edit, nodeName: e.target.value })}>
                        {/* Always offered so a pinned service can be reverted to scheduler-decides (empty → clears the pin). */}
                        <option value="">— scheduler decides —</option>
                        {(() => {
                          const names = (clusterNodes || []).map((n) => n.name);
                          // Keep the currently-saved node visible even if it's gone
                          // from the cluster, so the picker never hides the real value.
                          if (edit.nodeName && !names.includes(edit.nodeName)) names.unshift(edit.nodeName);
                          return names;
                        })().map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <div className="field-hint">
                        {clusterNodes === null ? 'Loading cluster nodes…'
                          : clusterNodes.length === 0 ? 'No cluster nodes reported.'
                          : `${clusterNodes.length} real cluster node${clusterNodes.length > 1 ? 's' : ''}. Pins this service to a specific node.`}
                      </div>
                    </div>
                    <div className="field">
                      <label className="field-label">CPU limit <span className="muted" style={{ fontWeight: 400 }}>· {CPU_MIN}m–{CPU_MAX}m</span></label>
                      <div className="input-row">
                        <input className={'input input-mono ' + (errors.cpu ? 'input-error' : '')}
                               type="number" step="100" min={CPU_MIN} max={CPU_MAX}
                               value={/\d/.test(edit.cpuLimit) ? parseCpu(edit.cpuLimit) : ''}
                               onChange={(e) => setEdit({ ...edit, cpuLimit: e.target.value === '' ? '' : `${e.target.value}m` })}/>
                        <span className="input-suffix">m</span>
                      </div>
                      {errors.cpu ? <div className="field-error">{errors.cpu}</div> :
                       <div className="field-hint">1000m = 1 vCPU · current usage {svc.cpuUsage >= 0 ? `${svc.cpuUsage}%` : 'n/a'}</div>}
                    </div>
                    <div className="field">
                      <label className="field-label">Memory limit <span className="muted" style={{ fontWeight: 400 }}>· {MEM_MIN_GI}Gi–{MEM_MAX_GI}Gi</span></label>
                      <div className="input-row">
                        <input className={'input input-mono ' + (errors.mem ? 'input-error' : '')}
                               type="number" step="0.5" min={MEM_MIN_GI} max={MEM_MAX_GI}
                               value={/\d/.test(edit.memLimit) ? parseMemGi(edit.memLimit) : ''}
                               onChange={(e) => setEdit({ ...edit, memLimit: e.target.value === '' ? '' : `${e.target.value}Gi` })}/>
                        <span className="input-suffix">Gi</span>
                      </div>
                      {errors.mem ? <div className="field-error">{errors.mem}</div> :
                       <div className="field-hint">Current usage {svc.memUsage >= 0 ? `${svc.memUsage}% of limit` : 'n/a'}</div>}
                    </div>
                    <div className="field">
                      <label className="field-label">Image version <span className="muted" style={{ fontWeight: 400 }}>· empty = chart default</span></label>
                      <input className={'input input-mono ' + (errors.imageTag ? 'input-error' : '')}
                             type="text" placeholder="chart default"
                             list={hasTagOptions ? tagListId : undefined}
                             value={edit.imageTag}
                             onChange={(e) => setEdit({ ...edit, imageTag: e.target.value.trim() })}/>
                      {hasTagOptions && (
                        <datalist id={tagListId}>
                          {imageTags!.tags!.map((t) => <option key={t} value={t}/>)}
                        </datalist>
                      )}
                      {errors.imageTag ? <div className="field-error">{errors.imageTag}</div> :
                       hasTagOptions ?
                         <div className="field-hint">{imageTags!.tags!.length} versions from {imageTags!.registry} — pick one or type any tag. Clear to revert to the chart default.</div> :
                         <div className="field-hint">Pin this service to an image tag, e.g. v1.0.1. Clear to revert to the chart default.</div>}
                    </div>
                  </div>

                  <div className="modal-foot" style={{ marginTop: 18, marginLeft: -22, marginRight: -22, marginBottom: -20, padding: '14px 22px' }}>
                    <span className="muted" style={{ fontSize: 12, marginRight: 'auto' }}>
                      {isDirty ? <><span style={{ color: 'var(--amber)' }}>●</span> pending changes</> : 'no changes'}
                    </span>
                    <button className="btn" onClick={cancelEdit}>Cancel</button>
                    <button className="btn btn-primary" disabled={!isDirty || hasErrors}
                            onClick={() => setConfirmOpen(true)}>
                      <I.Save size={13}/> Review & apply
                    </button>
                  </div>
                </div>
              )}

              {/* Generic asset slots (e.g. comfyui workflow). Driven by the
                  service's declared `assets` — no service-name branch. A
                  service with no slots renders nothing. */}
              {svc.assets && svc.assets.length > 0 && (
                <>
                  <div className="divider"></div>
                  {svc.assets.map((slot) => (
                    <AssetSlotWidget
                      key={slot.key}
                      client={client}
                      svc={svc}
                      slot={slot}
                      setState={setState}
                      toast={toast}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Modal open={confirmOpen} onClose={() => { if (!saving) setConfirmOpen(false); }}
             title="Apply changes"
             subtitle={`Updates ${svc.name} on ${client.namespace}.`}
             dismissable={!saving}
             footer={
               <>
                 <button className="btn" disabled={saving} onClick={() => setConfirmOpen(false)}>Cancel</button>
                 <button className="btn btn-primary" disabled={saving} onClick={applyChanges}>
                   {saving ? 'Saving…' : 'Commit & sync'}
                 </button>
               </>
             }>
        {edit && (
          <div>
            <div className="diff">
              {edit.replicas !== svc.replicas && (
                <div className="diff-row"><span className="lbl">replicas</span><span className="old mono">{svc.replicas}</span><span className="new mono">{edit.replicas}</span></div>
              )}
              {edit.cpuLimit !== svc.cpuLimit && (
                <div className="diff-row"><span className="lbl">cpu limit</span><span className="old mono">{svc.cpuLimit}</span><span className="new mono">{edit.cpuLimit}</span></div>
              )}
              {edit.memLimit !== svc.memLimit && (
                <div className="diff-row"><span className="lbl">memory limit</span><span className="old mono">{svc.memLimit}</span><span className="new mono">{edit.memLimit}</span></div>
              )}
              {edit.nodeName !== svc.nodeName && (
                <div className="diff-row"><span className="lbl">nodeName</span><span className="old mono">{svc.nodeName}</span><span className="new mono">{edit.nodeName}</span></div>
              )}
              {edit.imageTag !== (svc.imageTag ?? '') && (
                <div className="diff-row"><span className="lbl">imageTag</span><span className="old mono">{svc.imageTag || 'chart default'}</span><span className="new mono">{edit.imageTag || 'chart default'}</span></div>
              )}
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 14, lineHeight: 1.6 }}>
              A git commit will update <span className="mono" style={{ color: 'var(--text-1)' }}>charts/{svc.name}/values.yaml</span>.
              ArgoCD will detect the change within ~30s and roll out updated pods.
            </div>
          </div>
        )}
      </Modal>

      <Modal open={addOpen} onClose={() => { if (!addBusy) setAddOpen(false); }}
             title="Add service"
             subtitle={`Spawns a chart-backed service in ${client.namespace}`}
             dismissable={!addBusy}
             footer={
               <>
                 <button className="btn" disabled={addBusy} onClick={() => setAddOpen(false)}>Cancel</button>
                 <button className="btn btn-primary" disabled={addBusy || availableCharts.length === 0} onClick={submitAddService}>
                   {addBusy ? 'Adding…' : 'Add service'}
                 </button>
               </>
             }>
        <div className="field">
          <label className="field-label">Service</label>
          <select className="select" value={addForm.service}
                  onChange={(e) => {
                    const def = chartCatalog.find((c) => c.name === e.target.value);
                    setAddForm({
                      service: e.target.value,
                      replicas: def?.defaultReplicas || 1,
                      cpuLimit: defaultCpuStr(def?.defaultCpu ?? addForm.cpuLimit),
                      memLimit: defaultMemStr(def?.defaultMem ?? addForm.memLimit),
                    });
                  }}>
            {chartCatalog.length === 0
              ? <option value={addForm.service}>{addForm.service}</option>
              : availableCharts.length === 0
                ? <option value="">All catalog services already added</option>
                : availableCharts.map((c) => (
                    <option key={c.name} value={c.name}>
                      {(c.label || c.name)} ({c.chartName} {c.chartVersion} · {c.kind})
                    </option>
                  ))}
          </select>
          <div className="field-hint">
            {chartCatalog.length === 0 ? 'Loading chart catalog…'
              : availableCharts.length === 0
                ? 'This client already has every service in the catalog.'
                : `${availableCharts.length} of ${chartCatalog.length} chart${chartCatalog.length > 1 ? 's' : ''} available to add.`}
          </div>
        </div>
        <div className="field">
          <label className="field-label">Replicas</label>
          <input className="input input-mono" type="number" min={1} max={20}
                 value={Number.isNaN(addForm.replicas) ? '' : addForm.replicas}
                 onChange={(e) => setAddForm({ ...addForm, replicas: e.target.value === '' ? NaN : parseInt(e.target.value, 10) })}/>
        </div>
        <div className="field">
          <label className="field-label">CPU limit</label>
          {/* Plain numeric entry with a fixed unit suffix, matching Edit resources. */}
          <div className="input-row">
            <input className="input input-mono" type="number" step="100" min={100}
                   value={/\d/.test(addForm.cpuLimit) ? parseCpu(addForm.cpuLimit) : ''}
                   onChange={(e) => setAddForm({ ...addForm, cpuLimit: e.target.value === '' ? '' : `${e.target.value}m` })}/>
            <span className="input-suffix">m</span>
          </div>
          <div className="field-hint">1000m = 1 vCPU.</div>
        </div>
        <div className="field">
          <label className="field-label">Memory limit</label>
          <div className="input-row">
            <input className="input input-mono" type="number" step="0.5" min={0.25}
                   value={/\d/.test(addForm.memLimit) ? parseMemGi(addForm.memLimit) : ''}
                   onChange={(e) => setAddForm({ ...addForm, memLimit: e.target.value === '' ? '' : `${e.target.value}Gi` })}/>
            <span className="input-suffix">Gi</span>
          </div>
        </div>
        {addErr && <div className="field-error">{addErr}</div>}
      </Modal>

      <Modal open={logsOpen} onClose={() => setLogsOpen(false)}
             title={`Logs · ${svc.name}`}
             subtitle={`namespace ${client.namespace} · last 300 lines`}
             dismissable
             footer={
               <>
                 <button className="btn" disabled={logsLoading} onClick={loadLogs}>
                   {logsLoading ? 'Loading…' : 'Refresh'}
                 </button>
                 <button className="btn btn-primary" onClick={() => setLogsOpen(false)}>Close</button>
               </>
             }>
        {logsErr ? (
          <div className="field-error" style={{ marginBottom: 8 }}>{logsErr}</div>
        ) : null}
        <pre style={{
          background: 'var(--surface-2, #0b0d12)',
          color: 'var(--text-1, #d6deeb)',
          padding: 12,
          borderRadius: 6,
          maxHeight: '60vh',
          overflow: 'auto',
          fontSize: 11.5,
          lineHeight: 1.45,
          margin: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}>
          {logsLoading ? 'Fetching logs…' : logsText}
        </pre>
      </Modal>

      <MiniModal open={confirmDelSvc} onClose={() => { if (!deletingSvc) setConfirmDelSvc(false); }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Delete service?</div>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-2)' }}>
          Remove <span className="mono">{svc.name}</span> from <span className="mono">{client.id}</span>.
          GitOps re-renders and ArgoCD prunes the workload. This can’t be undone.
        </p>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="btn btn-ghost btn-sm" disabled={deletingSvc} onClick={() => setConfirmDelSvc(false)}>Cancel</button>
          <button className="btn btn-danger-solid btn-sm" disabled={deletingSvc} onClick={handleDeleteService}>
            {deletingSvc ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </MiniModal>

      {tab === 'config' && (
        client.services.length === 0
          ? <div className="card card-pad muted" style={{ fontSize: 13 }}>No services to configure yet.</div>
          : <LiveConfigPane client={client} svc={svc} activeService={activeService} setActiveService={setActiveService}/>
      )}

      {tab === 'activity' && (
        <ClientActivityTab client={client}/>
      )}

      {tab === 's3' && (
        <S3Card client={client}
                managedStorage={!!state.settings.managedStorageEnabled}
                />
      )}

      {tab === 'tokens' && (
        <ClientAPITokens client={client}/>
      )}

      {tab === 'gitops' && (
        <ClientGitOpsHistory client={client}/>
      )}

      {tab === 'danger' && (
        <ClientDangerZone client={client} setState={setState} onNav={onNav}/>
      )}
    </div>
  );
}

// relativeTime renders an RFC3339 timestamp as "Xs ago", "Xm ago", etc.
// Local copy to avoid cross-file imports between screens (the same helper
// also lives in AuthDash.tsx — drift is fine, both are demo-grade).
function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const dSec = Math.max(0, (Date.now() - t) / 1000);
  if (dSec < 60) return `${Math.round(dSec)}s ago`;
  if (dSec < 3600) return `${Math.round(dSec / 60)}m ago`;
  if (dSec < 86400) return `${Math.round(dSec / 3600)}h ago`;
  if (dSec < 30 * 86400) return `${Math.round(dSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ─── GITOPS HISTORY TAB ────────────────────────────────────────────────────
// Shows the last N commits in the GitOps repo that touched
// tenants/<id>/. Lets the admin audit BE-authored commits from the
// dashboard. Hovers/clicks open the commit in GitLab/GitHub when the
// repo URL is recognised.
function ClientGitOpsHistory({ client }: { client: Client }) {
  const { unlocked: operator } = useOperatorMode();
  const [data, setData] = useState<Awaited<ReturnType<typeof fetchGitOpsHistory>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchGitOpsHistory(client.id, 50)
      .then((r) => { if (!cancelled) { setData(r); setErr(null); } })
      .catch((e) => { if (!cancelled) setErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [client.id]);

  if (loading) return <div className="muted" style={{ padding: 16, fontSize: 13 }}>Loading…</div>;
  if (err) return <div className="field-error" style={{ padding: 12 }}>Failed to load gitops history: {err}</div>;
  if (!data) return null;

  if (!data.enabled) {
    return (
      <div className="card">
        <div className="card-pad">
          <Empty icon={<I.GitBranch size={28}/>} title="GitOps disabled"
                 message="GitOps isn’t enabled on this instance, so there’s no commit history to show."/>
        </div>
      </div>
    );
  }
  if (data.error) {
    return <div className="field-error" style={{ padding: 12 }}>git log: {data.error}</div>;
  }
  if (data.commits.length === 0) {
    return (
      <div className="card">
        <div className="card-pad">
          <Empty icon={<I.GitBranch size={28}/>} title="No commits yet"
                 message={`Nothing in tenants/${client.id}/ has been pushed. A fresh tenant create commits on first sync.`}/>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-head"><div>
        <h3>GitOps history</h3>
        <p>Commits that touched <span className="mono">tenants/{client.id}/</span> · newest first</p>
      </div></div>
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 90 }}>SHA</th>
            <th style={{ width: 100 }}>When</th>
            <th style={{ width: 180 }}>Author</th>
            <th>Message</th>
            <th style={{ width: 40, textAlign: 'right' }}></th>
          </tr>
        </thead>
        <tbody>
          {data.commits.map((c) => (
            <tr key={c.sha}>
              <td className="mono" style={{ fontSize: 11.5 }}>{c.short}</td>
              <td className="muted" style={{ fontSize: 12 }}>{relativeTime(c.time)}</td>
              <td>
                <div style={{ fontSize: 12.5 }}>{c.author}</div>
                <div className="muted" style={{ fontSize: 11 }}>{c.email}</div>
              </td>
              <td style={{ fontSize: 12.5 }}>{c.message}</td>
              <td style={{ textAlign: 'right' }}>
                {operator && c.browseUrl && (
                  <a className="btn btn-ghost btn-sm" href={c.browseUrl} target="_blank" rel="noopener" title="Open commit in GitLab/GitHub">
                    <I.ExtLink size={12}/>
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── API TOKENS TAB ─────────────────────────────────────────────────────────

function ClientAPITokens({ client }: { client: Client }) {
  const toast = useToast();
  const [tokens, setTokens] = useState<APIToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newPlain, setNewPlain] = useState<{ token: string; label: string } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<APIToken | null>(null);
  const [revoking, setRevoking] = useState(false);

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      const list = await fetchAPITokens(client.id);
      setTokens(list);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const doRevoke = async () => {
    const t = revokeTarget;
    if (!t || revoking) return;
    setRevoking(true);
    try {
      await revokeAPIToken(client.id, t.id);
      await reload();
      setRevokeTarget(null);
      toast?.success('Token revoked', t.label);
    } catch (e) {
      toast?.error('Revoke failed', e instanceof ApiError ? e.message : String(e));
    } finally {
      setRevoking(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [client.id]);

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>API tokens</h3>
          <p>Bearer tokens for {client.name}'s scripts to call <span className="mono">/api/v1/tenants/{client.id}/bypass-rules</span>.</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setAddOpen(true)}>
          <I.Plus size={13}/> Issue token
        </button>
      </div>

      {err && <div className="field-error" style={{ padding: 12 }}>{err}</div>}

      {!loading && tokens.length === 0 && (
        <Empty title="No tokens yet" message={`Issue one so ${client.name} can manage their own bypass rules via REST API.`}
               icon={<I.Lock size={28}/>}/>
      )}

      {tokens.length > 0 && (
        <table className="tbl">
          <thead>
            <tr>
              <th>Label</th>
              <th style={{ width: 140 }}>Created</th>
              <th style={{ width: 140 }}>Expires</th>
              <th style={{ width: 140 }}>Last used</th>
              <th style={{ width: 100 }}>Status</th>
              <th style={{ width: 64 }}></th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => {
              const revoked = !!t.revokedAt;
              const expired = !revoked && t.expiresAt && new Date(t.expiresAt) < new Date();
              return (
                <tr key={t.id} style={{ opacity: revoked || expired ? 0.55 : 1 }}>
                  <td><span className="mono" style={{ fontSize: 12 }}>{t.label}</span></td>
                  <td className="muted" style={{ fontSize: 11.5 }}>{shortDate(t.createdAt)}</td>
                  <td className="muted" style={{ fontSize: 11.5 }}>{t.expiresAt ? shortDate(t.expiresAt) : 'never'}</td>
                  <td className="muted" style={{ fontSize: 11.5 }}>{t.lastUsedAt ? shortDate(t.lastUsedAt) : '—'}</td>
                  <td>
                    {revoked ? <span className="badge" style={{ background: 'var(--red-bg)', color: 'var(--red)' }}>revoked</span>
                     : expired ? <span className="badge" style={{ background: 'var(--amber-bg)', color: 'var(--amber)' }}>expired</span>
                     : <span className="badge" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>active</span>}
                  </td>
                  <td>
                    {!revoked && (
                      <button className="btn btn-ghost btn-icon btn-sm" title="Revoke" aria-label="Revoke token"
                              onClick={() => setRevokeTarget(t)}>
                        <I.Trash size={12}/>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <CreateTokenModal open={addOpen} clientId={client.id}
                        onClose={() => setAddOpen(false)}
                        onCreated={async (resp) => {
                          setAddOpen(false);
                          setNewPlain({ token: resp.token, label: resp.label });
                          await reload();
                        }}/>

      <Modal open={newPlain !== null}
             onClose={() => setNewPlain(null)}
             title="Token created — copy it now"
             subtitle="This is the only time the plaintext is shown. Server only stores its SHA-256 hash."
             dismissable={true}
             footer={
               <button className="btn btn-primary" onClick={() => setNewPlain(null)}>I have saved it</button>
             }>
        {newPlain && (
          <div className="col" style={{ gap: 10 }}>
            <div className="muted" style={{ fontSize: 12.5 }}>Label: <span className="mono">{newPlain.label}</span></div>
            <div className="mono"
                 style={{ padding: 12, background: 'var(--bg-3)', borderRadius: 6,
                          fontSize: 12, wordBreak: 'break-all', userSelect: 'all' }}>
              {newPlain.token}
            </div>
            <button className="btn btn-sm" onClick={() => navigator.clipboard?.writeText(newPlain.token).then(() => toast?.success('Copied'))}>
              <I.Copy size={12}/> Copy to clipboard
            </button>
          </div>
        )}
      </Modal>

      <MiniModal open={!!revokeTarget} onClose={() => { if (!revoking) setRevokeTarget(null); }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Revoke token?</div>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-2)' }}>
          Revoke <span className="mono">{revokeTarget?.label}</span>. Any script using it stops working immediately. This can’t be undone.
        </p>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="btn btn-ghost btn-sm" disabled={revoking} onClick={() => setRevokeTarget(null)}>Cancel</button>
          <button className="btn btn-danger-solid btn-sm" disabled={revoking} onClick={doRevoke}>
            {revoking ? 'Revoking…' : 'Revoke'}
          </button>
        </div>
      </MiniModal>
    </div>
  );
}

function CreateTokenModal({
  open, clientId, onClose, onCreated,
}: {
  open: boolean;
  clientId: string;
  onClose: () => void;
  onCreated: (resp: CreateAPITokenResponse) => void | Promise<void>;
}) {
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [ttlDays, setTtlDays] = useState(90);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setLabel(''); setTtlDays(90); setSubmitting(false); }
  }, [open]);

  const submit = async () => {
    if (!label.trim()) return;
    setSubmitting(true);
    try {
      const days = Number.isFinite(ttlDays) && ttlDays > 0 ? ttlDays : 90;
      const resp = await createAPIToken(clientId, label.trim(), `${days * 24}h`);
      await onCreated(resp);
    } catch (e) {
      toast?.error('Issue failed', e instanceof ApiError ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}
           title="Issue API token"
           subtitle={`Bearer token for ${clientId} to authenticate against the REST API.`}
           footer={
             <>
               <button className="btn" onClick={onClose}>Cancel</button>
               <button className="btn btn-primary" disabled={!label.trim() || submitting} onClick={submit}>
                 {submitting ? 'Issuing…' : 'Issue token'}
               </button>
             </>
           }>
      <div className="col" style={{ gap: 12 }}>
        <div className="field">
          <label className="field-label">Label <span className="req-dot">*</span></label>
          <input className="input input-mono" placeholder="ci-bot, deploy-prod, …"
                 value={label} onChange={(e) => setLabel(e.target.value)}/>
          <div className="field-hint">Human label; shown in lists + audit trail. Plaintext never logged.</div>
        </div>
        <div className="field">
          <label className="field-label">Expires in (days)</label>
          <input className="input input-mono" type="number" min={1} max={3650}
                 value={Number.isNaN(ttlDays) ? '' : ttlDays}
                 onChange={(e) => setTtlDays(e.target.value === '' ? NaN : parseInt(e.target.value, 10))}/>
          <div className="field-hint">Default is 90 days. Shorter is safer; you can revoke any time.</div>
        </div>
      </div>
    </Modal>
  );
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString() + ' ' + d.toTimeString().slice(0, 5);
}

// ─── ACTIVITY TAB (real backend audit log filtered to this tenant) ──────────

function ClientActivityTab({ client }: { client: Client }) {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetchAudit({ target: `clients/${client.id}`, limit: 100 })
      .then((page) => { if (!cancelled) setEvents(page.items); })
      .catch((e) => { if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [client.id]);

  const relTime = (iso: string) => {
    const dSec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (dSec < 60) return `${Math.round(dSec)}s ago`;
    if (dSec < 3600) return `${Math.round(dSec / 60)}m ago`;
    if (dSec < 86400) return `${Math.round(dSec / 3600)}h ago`;
    return `${Math.round(dSec / 86400)}d ago`;
  };

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>Activity for {client.name}</h3>
          <p>{loading ? 'loading…' : `${events.length} recent events from audit log`}</p>
        </div>
      </div>
      {err && <div className="field-error" style={{ padding: 12 }}>{err}</div>}
      <div style={{ padding: '4px 22px 18px' }}>
        {events.map((e) => (
          <div className="feed-row" key={e.id}>
            <div className="feed-time">{relTime(e.createdAt)}</div>
            <div>
              <div>
                <span className="badge" style={e.actorType === 'admin' ? {} : { background: 'var(--blue-bg)', color: 'var(--blue)' }}>
                  {e.actorEmail}
                </span>
                <span className="muted" style={{ marginLeft: 8 }}>{e.action}</span>
              </div>
              <div className="mono" style={{ fontSize: 11.5, color: 'var(--accent)', marginTop: 2 }}>
                {e.target.replace(`clients/${client.id}`, '').replace(/^\//, '') || '(tenant root)'}
              </div>
              {e.payload && (
                <div className="muted mono" style={{ fontSize: 11.5, marginTop: 2 }}>
                  {JSON.stringify(e.payload)}
                </div>
              )}
            </div>
          </div>
        ))}
        {!loading && events.length === 0 && (
          <Empty title="No activity yet" message="Changes will appear here once you act on this tenant."
                 icon={<I.History size={28}/>}/>
        )}
      </div>
    </div>
  );
}

// LiveConfigPane shows the rendered Helm values that ArgoCD is currently
// applying for a service. Read-only — edits go through PATCH /services/{name}
// (the Edit resources flow), keeping the DB as the source of truth.
function LiveConfigPane({
  client, svc, activeService, setActiveService,
}: {
  client: Client;
  svc: ClientService;
  activeService: string;
  setActiveService: (n: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState<{ yaml: string; chartBacked: boolean; filename?: string; applicationName?: string; note?: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetchLiveConfig(client.id, svc.name)
      .then((c) => { if (!cancelled) setCfg(c); })
      .catch((e) => { if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [client.id, svc.name]);

  return (
    <div>
      <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {client.services.map((s) => (
          <button key={s.name}
                  className={'btn btn-sm ' + (activeService === s.name ? 'btn-primary' : '')}
                  onClick={() => setActiveService(s.name)}>
            <StatusDot status={s.status}/>
            {s.name}
          </button>
        ))}
      </div>

      <div className="card card-pad" style={{ marginBottom: 12, background: 'var(--blue-bg)', borderLeft: '3px solid var(--blue)' }}>
        <div className="row" style={{ gap: 8, fontSize: 12.5 }}>
          <I.Info size={14} style={{ color: 'var(--blue)' }}/>
          <span style={{ color: 'var(--text-2)' }}>
            <strong>Read-only</strong> — this is the rendered Helm values YAML ArgoCD applies for
            <span className="mono" style={{ margin: '0 4px' }}>{cfg?.applicationName || svc.name}</span>.
            To change values, use <strong>Edit resources</strong> on the Services tab — the DB is the source of truth and a save
            re-renders + patches the Application.
          </span>
        </div>
      </div>

      {loading && <div className="muted" style={{ padding: 20 }}>Loading rendered values…</div>}
      {err && <div className="badge badge-red" style={{ margin: 12 }}>Failed to load: {err}</div>}
      {!loading && !err && cfg && !cfg.chartBacked && (
        <div className="card card-pad muted" style={{ fontSize: 12.5 }}>
          {cfg.note || 'This service is not chart-backed; no live config to display.'}
        </div>
      )}
      {!loading && !err && cfg && cfg.chartBacked && (
        <ConfigEditor
          files={{ [cfg.filename || 'values.yaml']: cfg.yaml }}
          pvcPath="rendered helm values · read-only"
          readOnly
        />
      )}
    </div>
  );
}

function argocdSyncBadgeClass(sync: string): string {
  switch (sync) {
    case 'Synced': return 'badge-green';
    case 'OutOfSync': return 'badge-amber';
    case 'Unknown': return '';
    default: return 'badge-blue';
  }
}
function argocdSyncDotClass(sync: string): string {
  switch (sync) {
    case 'Synced': return 'healthy';
    case 'OutOfSync': return 'degraded';
    case 'Unknown': return 'unknown';
    default: return 'pending';
  }
}

function ClientOverviewTab({
  client, setState, argoServer, onChangePlan, onNav,
}: {
  client: Client;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
  argoServer: string;
  onChangePlan: () => void;
  onNav: (r: { tab?: string; service?: string }) => void;
}) {
  const { unlocked: operator } = useOperatorMode();
  const [argo, setArgo] = useState<{ sync: string; health: string; revision?: string; exists: boolean } | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchArgocdStatus(client.id)
      .then((s) => { if (!cancelled) setArgo(s); })
      .catch(() => { if (!cancelled) setArgo({ sync: 'Unknown', health: 'Unknown', exists: false }); });
    return () => { cancelled = true; };
  }, [client.id]);

  // Per-PVC disk usage (capacity + used). Polled so the panel reflects the
  // server-side refresh without a manual page reload.
  const [pvcs, setPvcs] = useState<PVCUsage[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    // Reset to the loading state when switching tenants so we never show the
    // previous tenant's rows while the new one loads (or if its first fetch errors).
    setPvcs(null);
    const load = () => fetchPVCUsage(client.id)
      .then((p) => { if (!cancelled) setPvcs(p); })
      // On error keep the last values on a blip, but if we never loaded (still
      // null) settle to empty so the panel doesn't hang on "Loading…" forever.
      .catch(() => { if (!cancelled) setPvcs((prev) => prev ?? []); });
    load();
    const id = setInterval(load, 5_000);
    const off = onVisible(load);
    return () => { cancelled = true; clearInterval(id); off(); };
  }, [client.id]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14 }}>
      <div className="col">
        <div className="card">
          <div className="card-head"><div><h3>Services</h3><p>{client.services.length} workloads in this tenant</p></div></div>
          <table className="tbl">
            <thead><tr><th>Service</th><th>Status</th><th>Replicas</th><th>CPU / Mem</th><th>Node</th><th></th></tr></thead>
            <tbody>
              {client.services.map((s) => (
                <tr key={s.name} className="row-link" onClick={() => onNav({ tab: 'services', service: s.name })}>
                  <td>
                    <div className="row">
                      <div style={{ width: 24, height: 24, borderRadius: 5, background: 'var(--bg-3)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ServiceIcon name={s.icon} size={12}/>
                      </div>
                      <span className="mono" style={{ fontSize: 12.5 }}>{s.name}</span>
                    </div>
                  </td>
                  <td><StatusBadge status={s.status}/></td>
                  <td className="mono num">{s.readyReplicas}/{s.replicas}</td>
                  <td>
                    <div style={{ display: 'grid', gridTemplateColumns: '60px 60px', gap: 6 }}>
                      <ResourceBar value={s.cpuUsage}/>
                      <ResourceBar value={s.memUsage}/>
                    </div>
                  </td>
                  <td className="mono muted" style={{ fontSize: 12 }}>{s.nodeName}</td>
                  <td><I.ChevronR size={14} style={{ color: 'var(--text-3)' }}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Storage — per-PVC capacity + used. Only shown once the tenant has
            PVC-backed volumes (empty on emptyDir-only / no metrics source).
            Gated behind operator mode: disk numbers are inexact, hidden from
            the client view. */}
        {operator && (
          <div className="card">
            <div className="card-head"><div><h3>Storage</h3><p>Per-volume disk usage across this tenant's PVCs</p></div></div>
            {pvcs === null ? (
              <div className="card-pad muted" style={{ fontSize: 12.5 }}>Loading…</div>
            ) : pvcs.length === 0 ? (
              <div className="empty">
                <div className="empty-icon"><I.Database size={26}/></div>
                No PVC-backed volumes yet (services use ephemeral storage, or no metrics source).
              </div>
            ) : (
              <div className="card-pad col" style={{ gap: 14 }}>
                {pvcs.map((v) => {
                  // usedBytes < 0 = n/a: the storage backend doesn't report real
                  // per-volume usage (node-local / no quota). Show n/a, not a
                  // misleading node-disk number. Capacity is the real PVC size.
                  const measured = v.usedBytes >= 0 && v.capacityBytes > 0;
                  const pct = measured ? Math.round(v.usedBytes * 100 / v.capacityBytes) : -1;
                  return (
                    <div key={v.name}>
                      <div className="spread" style={{ marginBottom: 5 }}>
                        <span className="mono" style={{ fontSize: 12.5, wordBreak: 'break-all' }}>{v.name}</span>
                        <span className="mono" style={{ fontSize: 12.5, flexShrink: 0, marginLeft: 10 }}>
                          {measured
                            ? <>{fmtBytes(v.usedBytes)} / {fmtBytes(v.capacityBytes)} <span className="muted">({pct}%)</span></>
                            : v.capacityBytes > 0
                              ? <><span className="muted">n/a</span> / {fmtBytes(v.capacityBytes)}</>
                              : <span className="muted">n/a</span>}
                        </span>
                      </div>
                      <ResourceBar value={pct}/>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <BasicAuthCard client={client}/>
      </div>
      <div className="col">
        <div className="card card-pad">
          <h3 style={{ margin: '0 0 12px', fontSize: 13 }}>Tenant info</h3>
          <div className="col" style={{ gap: 10, fontSize: 12.5 }}>
            <KVrow label="Namespace" value={<span className="mono">{client.namespace}</span>}/>
            <KVrow label="Plan" value={
              <span className="row" style={{ gap: 6 }}>
                <span className="badge">{client.plan}</span>
                <button className="btn btn-primary btn-sm" style={{ height: 'auto', padding: '1px 9px', fontSize: 12 }}
                        title="Change plan tier" onClick={onChangePlan}>
                  Change
                </button>
              </span>
            }/>
            <KVrow label="Contact" value={<span className="mono">{client.contactEmail}</span>}/>
            <KVrow label="Created" value={client.createdLabel}/>
          </div>
          <div className="divider"></div>
          <h4 style={{ margin: '0 0 8px', fontSize: 11.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>ArgoCD</h4>
          <div className="col" style={{ gap: 10, fontSize: 12.5 }}>
            <KVrow label="App" value={<span className="mono">{client.argoApp}</span>}/>
            <KVrow label="Branch" value={<span className="mono">{client.branch}</span>}/>
            <KVrow label="Sync" value={
              <span className={'badge ' + argocdSyncBadgeClass(argo?.sync || 'Unknown')}>
                <span className={'status-dot ' + argocdSyncDotClass(argo?.sync || 'Unknown')} style={{ boxShadow: 'none' }}/>
                {argo?.sync || 'Loading…'}
              </span>
            }/>
            <KVrow label="Health" value={
              <span className={'badge ' + argocdSyncBadgeClass(argo?.health === 'Healthy' ? 'Synced' : argo?.health === 'Degraded' ? 'OutOfSync' : argo?.health || 'Unknown')}>
                <span className={'status-dot ' + (argo?.health === 'Healthy' ? 'healthy' : argo?.health === 'Degraded' ? 'degraded' : argo?.health === 'Progressing' ? 'pending' : 'unknown')} style={{ boxShadow: 'none' }}/>
                {argo?.health || 'Loading…'}
              </span>
            }/>
            {argo?.revision && (
              <KVrow label="Revision" value={<span className="mono" style={{ fontSize: 11.5 }}>{argo.revision.slice(0, 10)}</span>}/>
            )}
          </div>
          {operator && (
            <button className="btn" style={{ marginTop: 14, width: '100%' }}
                    onClick={() => window.open(argocdAppURL(client.argoApp, argoServer), '_blank', 'noopener')}>
              <I.ExtLink size={12}/> Open in ArgoCD
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// BasicAuthCard manages per-client basic-auth user mappings. Each entry creates
// a BasicUser CR (username+password) in the tenant namespace; an external
// operator turns these into another app's config. Usernames are unique
// cluster-wide (BE rejects duplicates with 409).
// generatePassword produces a 12-char password guaranteed to contain at
// least one uppercase, one lowercase, one digit, and one symbol — what
// the BasicUser CRD operator typically enforces downstream. Uses
// `crypto.getRandomValues` (browser CSPRNG) so refreshing the generator
// gives unbiased entropy; falls back to Math.random in the unlikely
// case the API is unavailable.
function generatePassword(length = 12): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';   // no I, O — visually ambiguous
  const lower = 'abcdefghijkmnopqrstuvwxyz';  // no l
  const digits = '23456789';                   // no 0, 1
  const symbols = '!@#$%^&*-_=+?';
  const all = upper + lower + digits + symbols;

  // Pull crypto-strong random ints in [0, n).
  const rand = (n: number) => {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const buf = new Uint32Array(1);
      crypto.getRandomValues(buf);
      return buf[0] % n;
    }
    return Math.floor(Math.random() * n);
  };

  // Guarantee one of each class, then fill + shuffle.
  const chars = [
    upper[rand(upper.length)],
    lower[rand(lower.length)],
    digits[rand(digits.length)],
    symbols[rand(symbols.length)],
  ];
  for (let i = chars.length; i < length; i++) chars.push(all[rand(all.length)]);
  // Fisher–Yates shuffle so the guaranteed-class chars aren't always first.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = rand(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

// MiniModal is a lightweight centered panel — one tight padding, a single
// border, no head/body/foot chrome. Used for the small note + confirm popups
// where the heavier shared Modal feels oversized.
function MiniModal({ open, onClose, children, maxWidth = 340, pad = 16 }:
  { open: boolean; onClose: () => void; children: React.ReactNode; maxWidth?: number; pad?: number }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border-strong)', borderRadius: 10,
                    boxShadow: 'var(--shadow-pop)', width: '100%', maxWidth, padding: pad }}>
        {children}
      </div>
    </div>
  );
}

// NoteEditModal is the small popup for a BasicUser's note: a short title
// (always shown in the table) plus a longer free-form note. Used both when
// creating a user (onSave updates local draft state) and when editing an
// existing one (onSave persists via the API).
function NoteEditModal({
  open, onClose, subject, initialTitle, initialNote, onSave,
}: {
  open: boolean;
  onClose: () => void;
  subject: string;
  initialTitle: string;
  initialNote: string;
  onSave: (title: string, note: string) => Promise<void> | void;
}) {
  const [title, setTitle] = useState(initialTitle);
  const [note, setNote] = useState(initialNote);
  const [saving, setSaving] = useState(false);
  // Re-seed the fields each time the modal opens for a (possibly) different subject.
  useEffect(() => { if (open) { setTitle(initialTitle); setNote(initialNote); } }, [open, initialTitle, initialNote]);

  const commit = async (t: string, n: string) => {
    setSaving(true);
    try { await onSave(t, n); onClose(); }
    catch { /* caller surfaces the error toast; keep the modal open so input isn't lost */ }
    finally { setSaving(false); }
  };
  const save = () => commit(title.trim(), note.trim());
  const clear = () => commit('', ''); // delete the note entirely
  const hasNote = !!(title.trim() || note.trim());

  return (
    <MiniModal open={open} onClose={() => { if (!saving) onClose(); }} maxWidth={480} pad={12}>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>Note · {subject}</div>
      <input className="input" autoFocus value={title} maxLength={120} style={{ width: '100%' }}
             placeholder="Title (always shown)" onChange={(e) => setTitle(e.target.value)}/>
      <textarea className="input" value={note} maxLength={2000} rows={4}
                placeholder="Note…" style={{ width: '100%', resize: 'vertical', minHeight: 84, marginTop: 8, fontFamily: 'inherit' }}
                onChange={(e) => setNote(e.target.value)}/>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        {hasNote && <button className="btn btn-ghost btn-danger btn-sm" disabled={saving} onClick={clear}
                            style={{ marginRight: 'auto' }}>Delete</button>}
        <button className="btn btn-ghost btn-sm" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </MiniModal>
  );
}

// PasswordModal changes a BasicUser's password: a password field with a
// show/hide toggle + a generator, mirroring the create form's password control.
function PasswordModal({ open, onClose, username, onSave }: {
  open: boolean;
  onClose: () => void;
  username: string;
  onSave: (password: string) => Promise<void>;
}) {
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setPw(''); setShow(false); } }, [open]);

  const save = async () => {
    if (!pw) return;
    setSaving(true);
    try { await onSave(pw); onClose(); }
    catch { /* caller surfaces the error toast */ }
    finally { setSaving(false); }
  };

  return (
    <MiniModal open={open} onClose={() => { if (!saving) onClose(); }} maxWidth={380} pad={14}>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>Change password · {username}</div>
      <div className="row" style={{ gap: 6, alignItems: 'stretch' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <input className="input input-mono" style={{ paddingRight: 32, width: '100%' }}
                 type={show ? 'text' : 'password'} placeholder="new password" autoFocus
                 value={pw} disabled={saving} autoComplete="new-password"
                 onChange={(e) => setPw(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter' && pw && !saving) save(); }}/>
          <button type="button" className="btn btn-ghost btn-icon btn-sm"
                  title={show ? 'Hide password' : 'Show password'} onClick={() => setShow((v) => !v)}
                  style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', height: 22, padding: '0 4px' }}>
            {show ? <I.EyeOff size={13}/> : <I.Eye size={13}/>}
          </button>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" title="Generate a 12-char password"
                disabled={saving} onClick={() => { setPw(generatePassword(12)); setShow(true); }}>
          <I.Refresh size={12}/>
        </button>
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button className="btn btn-ghost btn-sm" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" disabled={saving || !pw} onClick={save}>{saving ? 'Saving…' : 'Change'}</button>
      </div>
    </MiniModal>
  );
}

// S3GrantsModal manages which S3 connections a basic-auth user may access and
// whether they have read and/or write on each. Save replaces the whole grant set.
function S3GrantsModal({ open, onClose, client, user }: {
  open: boolean;
  onClose: () => void;
  client: Client;
  user: BasicUser | null;
}) {
  const toast = useToast();
  const [configs, setConfigs] = useState<S3Config[] | null>(null);
  const [grants, setGrants] = useState<Record<string, { read: boolean; write: boolean }>>({});
  // Whether this user sees the client's services in the portal (default allowed).
  const [canViewServices, setCanViewServices] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false; // latest-wins: a fast reopen for another user must not apply this fetch
    setConfigs(null); setLoadErr(null);
    Promise.all([fetchS3Configs(client.id), fetchBasicUserS3Grants(client.id, user.username), fetchServiceAccess(client.id)])
      .then(([cfgs, gs, sa]) => {
        if (cancelled) return;
        setConfigs(cfgs);
        const m: Record<string, { read: boolean; write: boolean }> = {};
        for (const g of gs) m[g.s3ConfigId] = { read: g.canRead, write: g.canWrite };
        setGrants(m);
        const row = sa.find((e) => e.username === user.username);
        setCanViewServices(row ? row.canView : true); // no row = default allowed
      })
      // A load failure must NOT collapse to a false "empty" state — otherwise
      // Save would PUT empty arrays and wipe the user's real grants. Surface the
      // error and keep Save disabled (configs stays null → not "loaded").
      .catch((e) => { if (!cancelled) setLoadErr(e instanceof ApiError ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [open, user, client.id]);

  // Write implies read: enabling write also enables read, and disabling read
  // clears write (you can't have write-without-read).
  const toggle = (id: string, kind: 'read' | 'write') =>
    setGrants((prev) => {
      const cur = prev[id] ?? { read: false, write: false };
      if (kind === 'write') {
        const write = !cur.write;
        return { ...prev, [id]: { write, read: write ? true : cur.read } };
      }
      const read = !cur.read;
      return { ...prev, [id]: { read, write: read ? cur.write : false } };
    });

  const save = async () => {
    if (!user) return;
    setSaving(true);
    const payload: S3Grant[] = Object.entries(grants)
      .filter(([, v]) => v.read || v.write)
      .map(([id, v]) => ({ s3ConfigId: id, canRead: v.read, canWrite: v.write }));
    try {
      await Promise.all([
        setBasicUserS3Grants(client.id, user.username, payload),
        setUserServiceAccess(client.id, user.username, canViewServices),
      ]);
      toast?.success('Access saved', `${user.username}'s S3 access updated`);
      onClose();
    } catch (err) {
      toast?.error('Could not save access', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { if (!saving) onClose(); }}
           title={`S3 access · ${user?.username ?? ''}`} size="sm"
           footer={
             <>
               <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
               <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || configs === null || !!loadErr}>
                 {saving ? 'Saving…' : 'Save'}
               </button>
             </>
           }>
      {loadErr ? (
        <div className="field-error" style={{ padding: 10 }}>
          Couldn't load access for this user: {loadErr}. Close and reopen to retry — saving is disabled so existing grants aren't overwritten.
        </div>
      ) : configs === null ? (
        <div className="muted" style={{ fontSize: 12.5, padding: 8 }}>Loading…</div>
      ) : (
        <div className="col" style={{ gap: 14 }}>
          {/* Portal service visibility — per user, whole client (not per connection).
              Same plain-checkbox / .tbl styling as the by-connection modal's Services column. */}
          <div>
            <div className="field-label" style={{ marginBottom: 6 }}>Portal</div>
            <table className="tbl">
              <thead><tr><th>Access</th>
                <th style={{ width: 74, textAlign: 'center' }} title="Whether this user sees the client's services in the portal">Services</th></tr></thead>
              <tbody>
                <tr>
                  <td style={{ fontSize: 12.5 }}>See services in the portal</td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={canViewServices}
                           onChange={() => setCanViewServices((v) => !v)}/>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <div className="field-label" style={{ marginBottom: 6 }}>S3 connections · read / write</div>
            {configs.length === 0 ? (
              <div className="muted" style={{ fontSize: 12.5, padding: 8 }}>
                No S3 connections yet. Add one in the <b>S3</b> tab first.
              </div>
            ) : (
              <table className="tbl">
                <thead><tr><th>Connection</th>
                  <th style={{ width: 60, textAlign: 'center' }}>Read</th>
                  <th style={{ width: 60, textAlign: 'center' }}>Write</th></tr></thead>
                <tbody>
                  {configs.map((c) => {
                    const g = grants[c.id] ?? { read: false, write: false };
                    return (
                      <tr key={c.id}>
                        <td style={{ fontSize: 12.5 }}>
                          <div style={{ fontWeight: 600 }}>{c.name}</div>
                          <div className="mono muted" style={{ fontSize: 11 }}>{c.bucket || c.endpoint || '—'}</div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input type="checkbox" checked={g.read} disabled={g.write}
                                 title={g.write ? 'Write includes read' : undefined}
                                 onChange={() => toggle(c.id, 'read')}/>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input type="checkbox" checked={g.write} onChange={() => toggle(c.id, 'write')}/>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function BasicAuthCard({ client }: { client: Client }) {
  const toast = useToast();
  const [users, setUsers] = useState<BasicUser[] | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  // Draft note for the user about to be created.
  const [draftTitle, setDraftTitle] = useState('');
  const [draftNote, setDraftNote] = useState('');
  const [createNoteOpen, setCreateNoteOpen] = useState(false);
  // Portal visibility for the user about to be created. A new user is created
  // seeing nothing, so this is where that gets decided — asking here saves the
  // trip to the S3 access dialog that the setting otherwise only lives in.
  const [draftSeesServices, setDraftSeesServices] = useState(false);
  // The existing user whose note is being edited (null = closed).
  const [editUser, setEditUser] = useState<BasicUser | null>(null);
  // The user pending a delete confirmation (null = no dialog).
  const [confirmDelUser, setConfirmDelUser] = useState<string | null>(null);
  // The user whose password is being changed (null = closed).
  const [pwUser, setPwUser] = useState<BasicUser | null>(null);
  // The user whose S3 access grants are being managed (null = closed).
  const [grantsUser, setGrantsUser] = useState<BasicUser | null>(null);

  const reload = () => {
    fetchBasicUsers(client.id)
      .then((u) => setUsers(u))
      .catch(() => setUsers([]));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [client.id]);

  const add = async () => {
    const u = username.trim();
    if (!u || !password) { toast?.error('Missing fields', 'Username and password are required.'); return; }
    setBusy(true);
    try {
      await createBasicUser(client.id, u, password, draftTitle.trim() || undefined, draftNote.trim() || undefined);
      // Only when asked for: the server already creates the user seeing
      // nothing, so the unticked case needs no request. Not fatal — the user
      // exists either way, and the safe state is the one it is already in.
      if (draftSeesServices) {
        try {
          await setUserServiceAccess(client.id, u, true);
        } catch (e) {
          toast?.error('User added, but portal access was not set',
            `${e instanceof ApiError ? e.message : String(e)} — set it from the user's S3 access dialog.`);
        }
      }
    } catch (err) {
      setBusy(false);
      toast?.error('Could not add user',
        err instanceof ApiError ? err.message : String(err)); // 409 = duplicate username
      return;
    }
    setBusy(false);
    setUsername(''); setPassword(''); setShowPwd(false);
    setDraftTitle(''); setDraftNote(''); setDraftSeesServices(false);
    toast?.success('Basic-auth user added', u);
    reload();
  };

  const saveNote = async (u: string, title: string, note: string) => {
    try {
      await updateBasicUserNote(client.id, u, title, note);
      setUsers((prev) => prev?.map((x) => x.username === u ? { ...x, title, note } : x) ?? prev);
    } catch (err) {
      toast?.error('Could not save note', err instanceof ApiError ? err.message : String(err));
      throw err;
    }
  };

  const changePassword = async (u: string, password: string) => {
    try {
      await updateBasicUserPassword(client.id, u, password);
      toast?.success('Password changed', `${u}'s password was updated`);
    } catch (err) {
      toast?.error('Could not change password', err instanceof ApiError ? err.message : String(err));
      throw err;
    }
  };

  const remove = async (u: string) => {
    try {
      await deleteBasicUser(client.id, u);
      toast?.success('Removed', `${u} deleted`);
      reload();
    } catch (err) {
      toast?.error('Delete failed', err instanceof ApiError ? err.message : String(err));
    }
  };

  const onGenerate = () => {
    setPassword(generatePassword(12));
    setShowPwd(true); // operator should see what was generated, at least once
  };

  const onKey = (e: React.KeyboardEvent) => { if (e.key === 'Enter' && username.trim() && password && !busy) add(); };
  const hasDraftNote = !!(draftTitle.trim() || draftNote.trim());

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-head">
        <div>
          <h3>Basic auth users</h3>
          <p>HTTP basic-auth logins for this client (user + password) · username must be unique cluster-wide</p>
        </div>
      </div>

      <table className="tbl">
        <thead><tr><th>Username</th><th>Note</th><th style={{ width: 140 }}></th></tr></thead>
        <tbody>
          {users === null && (
            <tr><td colSpan={3} className="muted" style={{ padding: 14, fontSize: 12.5 }}>Loading…</td></tr>
          )}
          {users?.length === 0 && (
            <tr><td colSpan={3} className="muted" style={{ padding: 14, fontSize: 12.5 }}>No basic-auth users yet — add one with the form below.</td></tr>
          )}
          {users?.map((u) => (
            <tr key={u.username}>
              <td className="mono" style={{ fontSize: 12.5 }}>{u.username}</td>
              <td style={{ maxWidth: 240, fontSize: 12.5, color: u.title ? 'var(--text)' : 'var(--text-3)',
                           overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={u.note || u.title || ''}>
                {u.title || <span style={{ fontStyle: 'italic' }}>—</span>}
              </td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <button className="btn btn-ghost btn-icon btn-sm" title="Change password" onClick={() => setPwUser(u)}>
                  <I.Key size={13}/>
                </button>
                {/* Always available, like the Access button on a connection.
                    These are two views of the same grants — which one an
                    operator wants depends on what they are doing, not on a
                    setting, and hiding either only ever meant hunting for it. */}
                <button className="btn btn-ghost btn-icon btn-sm" title="S3 access" onClick={() => setGrantsUser(u)}>
                  <I.Database size={13}/>
                </button>
                <button className="btn btn-ghost btn-icon btn-sm"
                        title={u.title || u.note ? 'Edit note' : 'Add note'}
                        onClick={() => setEditUser(u)}>
                  <I.File size={13}/>
                </button>
                <button className="btn btn-ghost btn-icon btn-sm" title="Delete" onClick={() => setConfirmDelUser(u.username)}>
                  <I.Trash size={13}/>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="card-pad row" style={{ gap: 8, alignItems: 'center', borderTop: '1px solid var(--border)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input className="input" style={{ width: '100%' }} placeholder="username" value={username}
                 disabled={busy} autoComplete="off" onChange={(e) => setUsername(e.target.value)} onKeyDown={onKey}/>
        </div>
        {/* Password field with an inline eye toggle. Same flex basis as the
            username so both inputs share the row's width equally. */}
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <input className="input input-mono" style={{ paddingRight: 32, width: '100%' }}
                 placeholder="password" type={showPwd ? 'text' : 'password'}
                 value={password} disabled={busy} autoComplete="new-password"
                 onChange={(e) => setPassword(e.target.value)} onKeyDown={onKey}/>
          <button type="button" className="btn btn-ghost btn-icon btn-sm"
                  title={showPwd ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPwd((v) => !v)}
                  style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', height: 22, padding: '0 4px' }}>
            {showPwd ? <I.EyeOff size={13}/> : <I.Eye size={13}/>}
          </button>
        </div>
        {/* Generate (icon only), Note and Add sit on the same line, aligned. */}
        <button type="button" className="btn btn-ghost btn-icon"
                title="Generate a 12-char password (upper + lower + digit + symbol)"
                disabled={busy} onClick={onGenerate}>
          <I.Refresh size={14}/>
        </button>
        <button type="button" className={'btn ' + (hasDraftNote ? 'btn-primary' : 'btn-ghost')}
                title="Add an optional note (title + longer text)" disabled={busy}
                onClick={() => setCreateNoteOpen(true)}
                style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <I.File size={14}/> {hasDraftNote ? (draftTitle.trim() || 'Note added') : 'Note'}
        </button>
        <label className="row" style={{ gap: 6, alignItems: 'center', fontSize: 12.5, whiteSpace: 'nowrap' }}
               title="Whether this user sees the client's services — names, status, in-cluster addresses — in the portal. Off unless you ask for it.">
          <input type="checkbox" checked={draftSeesServices} disabled={busy}
                 onChange={() => setDraftSeesServices((v) => !v)}/>
          Sees services
        </label>
        <button className="btn btn-primary" disabled={busy || !username.trim() || !password} onClick={add}>
          {busy ? 'Adding…' : 'Add user'}
        </button>
      </div>

      <NoteEditModal open={createNoteOpen} onClose={() => setCreateNoteOpen(false)} subject="new user"
                     initialTitle={draftTitle} initialNote={draftNote}
                     onSave={(t, n) => { setDraftTitle(t); setDraftNote(n); }}/>
      <NoteEditModal open={!!editUser} onClose={() => setEditUser(null)} subject={editUser?.username ?? ''}
                     initialTitle={editUser?.title ?? ''} initialNote={editUser?.note ?? ''}
                     onSave={(t, n) => saveNote(editUser!.username, t, n)}/>
      <PasswordModal open={!!pwUser} onClose={() => setPwUser(null)} username={pwUser?.username ?? ''}
                     onSave={(pw) => changePassword(pwUser!.username, pw)}/>
      <S3GrantsModal open={!!grantsUser} onClose={() => setGrantsUser(null)} client={client} user={grantsUser}/>
      <MiniModal open={!!confirmDelUser} onClose={() => setConfirmDelUser(null)}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Delete user?</div>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-2)' }}>
          Remove <span className="mono">{confirmDelUser}</span> and its login. This can’t be undone.
        </p>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelUser(null)}>Cancel</button>
          <button className="btn btn-danger-solid btn-sm"
                  onClick={() => { const u = confirmDelUser; setConfirmDelUser(null); if (u) remove(u); }}>Delete</button>
        </div>
      </MiniModal>
    </div>
  );
}

// ─── S3 TAB ────────────────────────────────────────────────────────────────
// Per-client S3 connections (name + endpoint + access/secret key + region +
// bucket). Pure storage for now — nothing consumes these yet; per basic-auth
// user read/write permissions referencing them come later.

// Mirrors s3NoteMaxLen on the server, which answers 400 past it.
const S3_NOTE_MAX = 500;

const EMPTY_S3: S3ConfigInput = { name: '', note: '', endpoint: '', accessKey: '', secretKey: '', region: '', bucket: '' };

// Defaults for a NEW connection — pre-filled but fully editable. Points at
// Hetzner Object Storage (fsn1) since that's what we use; the operator can
// overwrite endpoint/region for a different provider/region.
const NEW_S3: S3ConfigInput = { ...EMPTY_S3, endpoint: 'https://fsn1.your-objectstorage.com', region: 'fsn1' };

// S3FormModal is the shared add/edit form for one S3 connection.
function S3FormModal({
  open, onClose, title, initial, onSave, nameOnly = false, quota,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  initial: S3ConfigInput;
  onSave: (v: S3ConfigInput) => Promise<void>;
  // nameOnly marks a managed bucket, where everything but the note is fixed.
  // Endpoint, bucket and credentials describe real storage, and the NAME is
  // what the real bucket name was derived from at creation — a bucket cannot be
  // renamed, so an editable name only moved the label away from the storage it
  // points at. The server ignores all of them, so an editable field would be a
  // lie. The note is what stays writable.
  nameOnly?: boolean;
  // Present only for managed buckets: the size limit is a property of storage
  // we own, so it is edited here beside the name rather than behind its own
  // icon. It writes through a separate endpoint, hence a separate callback.
  quota?: { bytes: number; onSave: (bytes: number) => Promise<void> };
}) {
  const [form, setForm] = useState<S3ConfigInput>(initial);
  const [showSecret, setShowSecret] = useState(false);
  const [busy, setBusy] = useState(false);
  const [quotaVal, setQuotaVal] = useState('');
  const [quotaUnit, setQuotaUnit] = useState<typeof QUOTA_UNITS[number]['label']>('GB');

  // Seed the form only on the closed→open transition — NOT on every re-render.
  // A parent re-render (e.g. App's 12s tenant poll) hands the edit modal a fresh
  // `initial` object identity; keying the effect on `initial` alone would re-run
  // it every tick and wipe the operator's in-progress edits (the reported
  // "bucket field keeps clearing" bug). The ref guard makes reopening re-seed
  // while a poll mid-edit does not.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setForm(initial); setShowSecret(false);
      // Seed the limit the same way, picking the unit that renders it simplest.
      const b = quota?.bytes ?? 0;
      if (b <= 0) { setQuotaVal(''); setQuotaUnit('GB'); }
      else {
        const u = [...QUOTA_UNITS].reverse().find((x) => b % x.bytes === 0 && b >= x.bytes) ?? QUOTA_UNITS[1];
        setQuotaUnit(u.label); setQuotaVal(String(b / u.bytes));
      }
    }
    wasOpen.current = open;
    /* eslint-disable-next-line */
  }, [open, initial]);

  const set = (k: keyof S3ConfigInput) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const quotaNum = Number(quotaVal);
  const quotaValid = quotaVal.trim() === '' || (Number.isFinite(quotaNum) && quotaNum > 0);
  const quotaBytes = quotaVal.trim() === ''
    ? 0
    : Math.round(quotaNum * QUOTA_UNITS.find((u) => u.label === quotaUnit)!.bytes);

  const save = async () => {
    if (!form.name.trim() || !quotaValid) return;
    setBusy(true);
    try {
      await onSave({ ...form, name: form.name.trim() });
      // Only when it actually moved: the limit writes to the storage server, and
      // a no-op save should not travel there just because the name changed.
      if (quota && quotaBytes !== quota.bytes) await quota.onSave(quotaBytes);
      onClose();
    } catch { /* toast surfaced by caller */ }
    setBusy(false);
  };

  const field = (label: string, node: React.ReactNode, hint?: string) => (
    <div style={{ marginBottom: 12 }}>
      <label className="field-label">{label}</label>
      {node}
      {hint && <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>{hint}</div>}
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={title} size="sm"
           footer={
             <>
               <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
               <button className="btn btn-primary btn-sm" onClick={save} disabled={busy || !form.name.trim() || !quotaValid}>
                 {busy ? 'Saving…' : 'Save'}
               </button>
             </>
           }>
      {nameOnly
        ? field('Name',
            <input className="input" style={{ width: '100%', cursor: 'not-allowed',
                                              background: 'var(--bg-2)', borderStyle: 'dashed',
                                              color: 'var(--text-2)' }}
                   value={form.name} readOnly disabled
                   title="Fixed: the bucket was named from this and buckets cannot be renamed"/>,
            'Fixed when the bucket was created — the bucket name came from it, and storage cannot be renamed. Use the note below.')
        : field('Name', <input className="input" style={{ width: '100%' }} placeholder="prod-assets"
                               autoFocus value={form.name} onChange={set('name')}/>)}
      {field('Note',
        <input className="input" style={{ width: '100%' }} placeholder="optional" maxLength={S3_NOTE_MAX}
               autoFocus={nameOnly} value={form.note} onChange={set('note')}/>,
        'Free text, shown in the list. Change it any time.')}
      {quota && (
        <div style={{ marginBottom: 12 }}>
          <label className="field-label">Size limit</label>
          <div className="row" style={{ gap: 6 }}>
            <input className="input" style={{ flex: 1 }} inputMode="decimal" placeholder="unlimited"
                   value={quotaVal} onChange={(e) => setQuotaVal(e.target.value)}/>
            <select className="input" style={{ width: 84 }} value={quotaUnit}
                    onChange={(e) => setQuotaUnit(e.target.value as typeof quotaUnit)}>
              {QUOTA_UNITS.map((u) => <option key={u.label} value={u.label}>{u.label}</option>)}
            </select>
          </div>
          {!quotaValid
            ? <div className="field-error" style={{ fontSize: 11.5, marginTop: 5 }}>Enter a positive number, or leave empty for no limit.</div>
            : <div className="muted" style={{ fontSize: 11.5, marginTop: 5 }}>
                {quotaBytes > 0
                  ? <>Uploads stop shortly after the bucket passes <b>{fmtBytes(quotaBytes)}</b> — a burst can
                     land past it before it is noticed, then further uploads are refused until the bucket is
                     back under. Existing data is never deleted; deleting some brings uploads back on its own.</>
                  : 'Empty means no limit.'}
              </div>}
        </div>
      )}
      {nameOnly && (
        <div className="muted" style={{ fontSize: 11.5, marginBottom: 12 }}>
          This bucket lives on the platform storage, so its endpoint and keys aren't editable here.
          Replace the key from the credentials dialog.
        </div>
      )}
      {!nameOnly && <>
      {field('Endpoint', <input className="input input-mono" style={{ width: '100%' }}
                                placeholder="s3.eu-central-1.amazonaws.com" value={form.endpoint} onChange={set('endpoint')}/>)}
      {field('Access key', <input className="input input-mono" style={{ width: '100%' }}
                                  autoComplete="off" value={form.accessKey} onChange={set('accessKey')}/>)}
      {field('Secret key',
        <div style={{ position: 'relative' }}>
          <input className="input input-mono" style={{ paddingRight: 32, width: '100%' }}
                 type={showSecret ? 'text' : 'password'} autoComplete="new-password"
                 value={form.secretKey} onChange={set('secretKey')}/>
          <button type="button" className="btn btn-ghost btn-icon btn-sm"
                  title={showSecret ? 'Hide' : 'Show'} onClick={() => setShowSecret((v) => !v)}
                  style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', height: 22, padding: '0 4px' }}>
            {showSecret ? <I.EyeOff size={13}/> : <I.Eye size={13}/>}
          </button>
        </div>)}
      <div className="row" style={{ gap: 10 }}>
        <div style={{ flex: 1 }}>{field('Region', <input className="input input-mono" style={{ width: '100%' }}
                                                         placeholder="eu-central-1" value={form.region} onChange={set('region')}/>)}</div>
        <div style={{ flex: 1 }}>{field('Bucket', <input className="input input-mono" style={{ width: '100%' }}
                                                         placeholder="acme-prod" value={form.bucket} onChange={set('bucket')}/>)}</div>
      </div>
      </>}
    </Modal>
  );
}

// S3ConnectionGrantsModal is the reverse of S3GrantsModal: for one connection it
// lists all the tenant's basic-auth users and lets you pick who has read/write.
function S3ConnectionGrantsModal({ open, onClose, client, config }: {
  open: boolean;
  onClose: () => void;
  client: Client;
  config: S3Config | null;
}) {
  const toast = useToast();
  const [users, setUsers] = useState<BasicUser[] | null>(null);
  const [grants, setGrants] = useState<Record<string, { read: boolean; write: boolean }>>({});
  const [accounts, setAccounts] = useState<S3Account[]>([]);
  const [accountGrants, setAccountGrants] = useState<Record<string, { read: boolean; write: boolean }>>({});
  // Portal visibility is NOT edited here. It is one setting for the whole
  // client, so a per-connection dialog implied a scope it does not have —
  // turning it on for one connection turned it on everywhere, which is not
  // what the column looked like it did. It lives on the user instead.
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !config) return;
    let cancelled = false; // latest-wins: a fast reopen for another connection must not apply this fetch
    setUsers(null); setLoadErr(null);
    Promise.all([
      fetchBasicUsers(client.id),
      fetchS3ConfigGrants(client.id, config.id),
      fetchS3Accounts(),
      fetchS3ConfigAccountGrants(client.id, config.id),
    ])
      .then(([us, gs, accs, ags]) => {
        if (cancelled) return;
        setUsers(us);
        const m: Record<string, { read: boolean; write: boolean }> = {};
        for (const g of gs) m[g.username] = { read: g.canRead, write: g.canWrite };
        setGrants(m);
        setAccounts(accs);
        const am: Record<string, { read: boolean; write: boolean }> = {};
        for (const g of ags) am[g.s3AccountId] = { read: g.canRead, write: g.canWrite };
        setAccountGrants(am);
      })
      // A load failure must NOT collapse to a false "empty" state — otherwise
      // Save would PUT empty arrays and wipe every existing grant. Surface the
      // error and keep Save disabled (users stays null → not "loaded").
      .catch((e) => { if (!cancelled) setLoadErr(e instanceof ApiError ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [open, config, client.id]);

  // Write implies read (same rule as the per-user modal). Shared toggler for
  // both the user grants and the external-account grants.
  const mkToggle = (setter: React.Dispatch<React.SetStateAction<Record<string, { read: boolean; write: boolean }>>>) =>
    (key: string, kind: 'read' | 'write') =>
      setter((prev) => {
        const cur = prev[key] ?? { read: false, write: false };
        if (kind === 'write') {
          const write = !cur.write;
          return { ...prev, [key]: { write, read: write ? true : cur.read } };
        }
        const read = !cur.read;
        return { ...prev, [key]: { read, write: read ? cur.write : false } };
      });
  const toggle = mkToggle(setGrants);
  const toggleAccount = mkToggle(setAccountGrants);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    const userPayload: S3ConfigGrant[] = Object.entries(grants)
      .filter(([, v]) => v.read || v.write)
      .map(([username, v]) => ({ username, canRead: v.read, canWrite: v.write }));
    const accountPayload: S3ConfigAccountGrant[] = Object.entries(accountGrants)
      .filter(([, v]) => v.read || v.write)
      .map(([s3AccountId, v]) => ({ s3AccountId, canRead: v.read, canWrite: v.write }));
    try {
      await Promise.all([
        setS3ConfigGrants(client.id, config.id, userPayload),
        setS3ConfigAccountGrants(client.id, config.id, accountPayload),
      ]);
      toast?.success('Access saved', `${config.name} access updated`);
      onClose();
    } catch (err) {
      toast?.error('Could not save access', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const rwCols = (
    <>
      <th style={{ width: 60, textAlign: 'center' }}>Read</th>
      <th style={{ width: 60, textAlign: 'center' }}>Write</th>
    </>
  );
  const rwCells = (g: { read: boolean; write: boolean }, onToggle: (kind: 'read' | 'write') => void) => (
    <>
      <td style={{ textAlign: 'center' }}>
        <input type="checkbox" checked={g.read} disabled={g.write}
               title={g.write ? 'Write includes read' : undefined}
               onChange={() => onToggle('read')}/>
      </td>
      <td style={{ textAlign: 'center' }}>
        <input type="checkbox" checked={g.write} onChange={() => onToggle('write')}/>
      </td>
    </>
  );

  return (
    <Modal open={open} onClose={() => { if (!saving) onClose(); }}
           title={`S3 access · ${config?.name ?? ''}`} size="sm"
           footer={
             <>
               <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
               <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || users === null || !!loadErr}>
                 {saving ? 'Saving…' : 'Save'}
               </button>
             </>
           }>
      {loadErr ? (
        <div className="field-error" style={{ padding: 10 }}>
          Couldn't load access for this connection: {loadErr}. Close and reopen to retry — saving is disabled so existing grants aren't overwritten.
        </div>
      ) : users === null ? (
        <div className="muted" style={{ fontSize: 12.5, padding: 8 }}>Loading…</div>
      ) : (
        <div className="col" style={{ gap: 16 }}>
          {/* Basic-auth users of this tenant */}
          <div>
            <div className="field-label" style={{ marginBottom: 6 }}>Basic-auth users</div>
            <div className="muted" style={{ fontSize: 11.5, marginBottom: 6 }}>
              Who may use <b>this connection</b>. The same grants seen from the other side — which connections
              one user may use — are on the user, under <b>S3 access</b> on the Overview tab.
            </div>
            {users.length === 0 ? (
              <div className="muted" style={{ fontSize: 12.5, padding: 8 }}>
                No basic-auth users yet. Add one under the tenant's <b>Overview</b> first.
              </div>
            ) : (
              <table className="tbl">
                <thead><tr><th>User</th>{rwCols}</tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.username}>
                      <td className="mono" style={{ fontSize: 12.5 }}>{u.username}</td>
                      {rwCells(grants[u.username] ?? { read: false, write: false }, (k) => toggle(u.username, k))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Global external S3 accounts */}
          <div>
            <div className="field-label" style={{ marginBottom: 6 }}>
              External accounts <span className="muted" style={{ fontWeight: 400 }}>· from Global S3 accounts</span>
            </div>
            {accounts.length === 0 ? (
              <div className="muted" style={{ fontSize: 12.5, padding: 8 }}>
                No accounts yet. Add external users under <b>Global S3 accounts</b> in the sidebar.
              </div>
            ) : (
              <table className="tbl">
                <thead><tr><th>Account</th>{rwCols}</tr></thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a.id}>
                      <td style={{ fontSize: 12.5 }}>
                        {a.name}{a.note ? <span className="muted" style={{ fontSize: 11.5 }}> · {a.note}</span> : null}
                      </td>
                      {rwCells(accountGrants[a.id] ?? { read: false, write: false }, (k) => toggleAccount(a.id, k))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── MANAGED BUCKETS ───────────────────────────────────────────────────────
// A managed bucket is storage we really provision on the platform's MinIO: the
// bucket, a user whose policy scopes it to that bucket alone, and an optional
// hard quota. Contrast with the externally-registered connections above, which
// are only the recorded coordinates of somebody else's S3.

// quotaUnits drives the size picker. MinIO enforces bytes, so these are the
// binary multiples the numbers are converted through.
const QUOTA_UNITS = [
  { label: 'MB', bytes: 1 << 20 },
  { label: 'GB', bytes: 1 << 30 },
  { label: 'TB', bytes: 2 ** 40 },
] as const;

// NewBucketModal provisions a bucket. Everything it collects — name and quota —
// is applied on the storage server before the connection row exists, so a
// failure here leaves nothing behind.
function NewBucketModal({ open, onClose, client, onCreated }: {
  open: boolean;
  onClose: () => void;
  client: Client;
  onCreated: (res: ProvisionedBucket) => void;
  // Whether this deployment has a server of its own, which decides if the
  // picker's empty option is a real default or a "pick one" placeholder.
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [quota, setQuota] = useState('');
  const [unit, setUnit] = useState<typeof QUOTA_UNITS[number]['label']>('GB');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ── Access (optional) ─────────────────────────────────────────────────────
  // Who may use the bucket, decided while creating it instead of in a second
  // trip through the Access dialog. Two kinds of subject, because they are two
  // different things: a basic-auth user belongs to THIS tenant and logs into
  // its portal, a global account is one external identity reused across
  // tenants. The third way — minting a basic-auth user right here — exists
  // because the common case (a person at this client who doesn't have a login
  // yet) otherwise means leaving the dialog to go create one.
  // The servers the operator registered, and which one this bucket goes on.
  // '' means nothing picked yet and never "use the default": a bucket cannot be
  // moved afterwards, so where it lands is the one field here worth refusing to
  // guess at.
  const [servers, setServers] = useState<StorageServer[]>([]);
  const [serverId, setServerId] = useState('');
  const [users, setUsers] = useState<BasicUser[]>([]);
  const [accounts, setAccounts] = useState<S3Account[]>([]);
  const [grants, setGrants] = useState<Record<string, { read: boolean; write: boolean }>>({});
  const [accountGrants, setAccountGrants] = useState<Record<string, { read: boolean; write: boolean }>>({});
  // Worth offering here: once the bucket exists the name is frozen, so the note
  // is the only label that stays changeable.
  const [note, setNote] = useState('');
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newUserRW, setNewUserRW] = useState({ read: true, write: true });
  // Portal visibility is deliberately NOT offered here. It is one setting per
  // user for the WHOLE client, so a per-bucket dialog is the wrong place to
  // change it — sitting beside Read/Write it read as if it applied to this
  // bucket only. It lives where its scope is honest: the per-user "S3 access"
  // dialog on the Overview tab, and the Add-user form beside it.
  // A failure to load the two lists must not block creating the bucket — the
  // access part is optional, so it degrades to "not offered" rather than fatal.
  const [accessLoadErr, setAccessLoadErr] = useState<string | null>(null);

  // Reset on each open so a previous attempt's values don't reappear.
  useEffect(() => {
    if (!open) return;
    setName(''); setNote(''); setQuota(''); setUnit('GB'); setErr(null);
    setGrants({}); setAccountGrants({});
    setNewUserOpen(false); setNewUsername(''); setNewPassword(''); setNewUserRW({ read: true, write: true });
    setUsers([]); setAccounts([]); setAccessLoadErr(null);
    setServers([]); setServerId('');
    let cancelled = false;
    Promise.all([fetchBasicUsers(client.id), fetchS3Accounts(), fetchStorageServers()])
      .then(([us, accs, srv]) => { if (!cancelled) { setUsers(us); setAccounts(accs); setServers(srv); } })
      .catch((e) => { if (!cancelled) setAccessLoadErr(e instanceof ApiError ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [open, client.id]);

  // Only servers the operator registered. The one configured through the
  // environment is deliberately not offered: it has no row, so it cannot be
  // named, tested or edited, and offering it here made one physical server
  // appear twice under two different names. Buckets created before servers were
  // registrable still live on it and still resolve — that is a property of
  // those rows, not a place to put new ones.
  const serverPicked = serverId !== '';

  // Write implies read — same rule as the Access dialog, so a subject granted
  // here and edited there behaves identically.
  const mkToggle = (setter: React.Dispatch<React.SetStateAction<Record<string, { read: boolean; write: boolean }>>>) =>
    (key: string, kind: 'read' | 'write') =>
      setter((prev) => {
        const cur = prev[key] ?? { read: false, write: false };
        if (kind === 'write') {
          const write = !cur.write;
          return { ...prev, [key]: { write, read: write ? true : cur.read } };
        }
        const read = !cur.read;
        return { ...prev, [key]: { read, write: read ? cur.write : false } };
      });
  const toggleUser = mkToggle(setGrants);
  const toggleAccount = mkToggle(setAccountGrants);

  const unitBytes = QUOTA_UNITS.find((u) => u.label === unit)!.bytes;
  const quotaNum = Number(quota);
  const quotaValid = quota.trim() === '' || (Number.isFinite(quotaNum) && quotaNum > 0);
  const quotaBytes = quota.trim() === '' ? 0 : Math.round(quotaNum * unitBytes);

  // Mirrors the server's derivation (miniostore.BucketName) so the operator sees
  // the real bucket name before committing. Each half is slugified separately
  // and BOTH must survive: joining first would let a name of pure punctuation
  // vanish and preview the bare tenant id — a bucket the server would refuse to
  // create. Empty here means "this name is unusable", which also blocks submit.
  // NFD splits an accented letter into letter + combining mark, so dropping the
  // marks (\p{Mn}) leaves the base letter — matching the server's foldDiacritics
  // exactly, so "zálohy" previews as "zalohy" and not "z-lohy".
  const slugify = (v: string) =>
    v.normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const nameSlug = slugify(name);
  const fullSlug = nameSlug && slugify(client.id) ? `${slugify(client.id)}--${nameSlug}` : '';
  // Past 63 characters the server shortens the name and appends a hash of the
  // original, which cannot be reproduced here. Showing a plain truncation was
  // worse than showing nothing: it named a bucket that would not exist, exactly
  // where the real name is hardest to guess. Too long is treated as invalid so
  // the operator shortens the name instead.
  const nameTooLong = fullSlug.length > 63;
  const previewBucket = nameTooLong ? '' : fullSlug;

  // A half-filled inline user is a mistake, not an intent to skip it.
  const newUserPartial = newUserOpen && (!!newUsername.trim() !== !!newPassword);
  const newUserReady = newUserOpen && !!newUsername.trim() && !!newPassword;

  const submit = async () => {
    if (!name.trim() || !previewBucket || !quotaValid || newUserPartial) return;
    setBusy(true); setErr(null);

    // The new basic-auth user goes FIRST, on purpose. Its username has to be
    // unique across the whole cluster, so it is the step most likely to be
    // rejected — and failing before the bucket exists leaves nothing behind.
    // The reverse order would strand a bucket whose name can never be reused.
    let createdUser: string | null = null;
    if (newUserReady) {
      try {
        await createBasicUser(client.id, newUsername.trim(), newPassword);
        createdUser = newUsername.trim();
      } catch (e) {
        setErr(`Could not create the user ${newUsername.trim()}: ${e instanceof ApiError ? e.message : String(e)}`);
        setBusy(false);
        return;
      }
    }

    let res: ProvisionedBucket;
    try {
      res = await provisionS3Bucket(client.id, {
        name: name.trim(), note: note.trim(), quotaBytes,
        serverId,
      });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      // Own up to the user we just made, or the operator is left wondering
      // where it came from when they retry.
      setErr(createdUser
        ? `${msg} — the basic-auth user ${createdUser} was already created and is still there.`
        : msg);
      setBusy(false);
      return;
    }

    // Grants come last and are NOT fatal: the bucket and its credentials exist
    // by now, and losing the secret behind an error screen would be far worse
    // than an access list the operator can fix from the Access dialog.
    const userPayload: S3ConfigGrant[] = Object.entries(grants)
      .filter(([, v]) => v.read || v.write)
      .map(([username, v]) => ({ username, canRead: v.read, canWrite: v.write }));
    if (createdUser && (newUserRW.read || newUserRW.write)) {
      userPayload.push({ username: createdUser, canRead: newUserRW.read, canWrite: newUserRW.write });
    }
    const accountPayload: S3ConfigAccountGrant[] = Object.entries(accountGrants)
      .filter(([, v]) => v.read || v.write)
      .map(([s3AccountId, v]) => ({ s3AccountId, canRead: v.read, canWrite: v.write }));

    if (userPayload.length || accountPayload.length) {
      try {
        await Promise.all([
          ...(userPayload.length ? [setS3ConfigGrants(client.id, res.connection.id, userPayload)] : []),
          ...(accountPayload.length ? [setS3ConfigAccountGrants(client.id, res.connection.id, accountPayload)] : []),
        ]);
        toast?.success('Bucket created', `${res.bucket} · ${userPayload.length + accountPayload.length} granted`);
      } catch (e) {
        toast?.error('Bucket created, but access was not saved',
          `${e instanceof ApiError ? e.message : String(e)} — set it from the Access button.`);
      }
    } else {
      toast?.success('Bucket created', res.bucket);
    }
    setBusy(false);
    onCreated(res);
  };

  return (
    <Modal open={open} onClose={() => { if (!busy) onClose(); }}
           title="New bucket" maxWidth={500}
           subtitle="Creates the bucket, a user restricted to it, and an optional size limit."
           footer={
             <>
               <button className="btn" disabled={busy} onClick={onClose}>Cancel</button>
               <button className="btn btn-primary"
                       disabled={busy || !previewBucket || !quotaValid || newUserPartial || !serverPicked}
                       title={!serverPicked ? 'Pick the managed S3 server this bucket is created on' : undefined}
                       onClick={submit}>
                 {busy ? 'Creating…' : 'Create bucket'}
               </button>
             </>
           }>
      <div className="col" style={{ gap: 12 }}>
        <div>
          <div className="field-label">Name</div>
          <input className="input" autoFocus placeholder="media" value={name}
                 onChange={(e) => { setName(e.target.value); setErr(null); }}
                 onKeyDown={(e) => { if (e.key === 'Enter' && name.trim() && quotaValid && !busy) submit(); }}/>
          {!name.trim()
            ? <div className="muted" style={{ fontSize: 11.5, marginTop: 5 }}>
                The bucket name is derived from the client id and this name.
              </div>
            : previewBucket
              ? <div className="muted" style={{ fontSize: 11.5, marginTop: 5 }}>
                  Bucket will be <span className="mono">{previewBucket}</span>
                </div>
              : nameTooLong
                ? <div className="field-error" style={{ fontSize: 11.5, marginTop: 5 }}>
                    Too long — the bucket name may be at most 63 characters and this one would be
                    {' '}{fullSlug.length}. Shorten it by {fullSlug.length - 63}.
                  </div>
                : <div className="field-error" style={{ fontSize: 11.5, marginTop: 5 }}>
                    Needs at least one letter or digit — this name can't become a bucket name.
                  </div>}
        </div>

        <div>
          <div className="field-label">Note <span className="muted" style={{ fontWeight: 400 }}>· optional</span></div>
          <input className="input" style={{ width: '100%' }} placeholder="what this bucket is for"
                 maxLength={S3_NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)}/>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 5 }}>
            The name above is fixed once the bucket exists — storage cannot be renamed. This note can be
            changed any time.
          </div>
        </div>

        <div>
          <div className="field-label">Managed S3 server</div>
          {servers.length === 0 ? (
            <div className="field-error" style={{ fontSize: 11.5, padding: 8 }}>
              No managed S3 servers registered yet — add one under <b>Managed S3 servers</b> first.
              A bucket has to be created on a server you have registered.
            </div>
          ) : (
            <>
              <select className="input" style={{ width: '100%' }} value={serverId}
                      onChange={(e) => { setServerId(e.target.value); setErr(null); }}>
                <option value="">Select a server…</option>
                {servers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} · {s.endpoint}</option>
                ))}
              </select>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 5 }}>
                Where the bucket is created. It stays on this server for its whole life — moving it later
                would mean copying the data, which this screen does not do.
              </div>
            </>
          )}
        </div>

        <div>
          <div className="field-label">Size limit</div>
          <div className="row" style={{ gap: 6 }}>
            <input className="input" style={{ flex: 1 }} inputMode="decimal" placeholder="unlimited"
                   value={quota} onChange={(e) => { setQuota(e.target.value); setErr(null); }}/>
            <select className="input" style={{ width: 84 }} value={unit}
                    onChange={(e) => { setUnit(e.target.value as typeof unit); setErr(null); }}>
              {QUOTA_UNITS.map((u) => <option key={u.label} value={u.label}>{u.label}</option>)}
            </select>
          </div>
          {!quotaValid
            ? <div className="field-error" style={{ fontSize: 11.5, marginTop: 5 }}>Enter a positive number, or leave empty for no limit.</div>
            : <div className="muted" style={{ fontSize: 11.5, marginTop: 5 }}>
                {quotaBytes > 0
                  ? <>Uploads stop shortly after the bucket passes <b>{fmtBytes(quotaBytes)}</b> — a burst can
                     land past it before it is noticed. Reading and deleting keep working, and deleting
                     something brings uploads back on their own.</>
                  : 'Leave empty for no limit. You can change this later.'}
              </div>}
        </div>

        <div className="divider" style={{ margin: '2px 0' }}></div>

        <div>
          <div className="field-label">Access <span className="muted" style={{ fontWeight: 400 }}>· optional</span></div>
          <div className="muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
            Who may use this bucket. You can leave it empty and set it later with the <b>Access</b> button.
            {' '}Read/Write applies to <b>this bucket</b>. Whether someone sees the client's services in the
            portal is a separate, client-wide setting — it lives on the user, under <b>S3 access</b> on the
            <b> Overview</b> tab.
          </div>

          {accessLoadErr && (
            <div className="field-error" style={{ fontSize: 11.5, marginBottom: 8 }}>
              Couldn't load the lists ({accessLoadErr}). The bucket can still be created — grant access afterwards.
            </div>
          )}

          {users.length > 0 && (
            <table className="tbl" style={{ marginBottom: 10 }}>
              <thead><tr>
                <th>Basic-auth user</th>
                <th style={{ width: 60, textAlign: 'center' }}>Read</th>
                <th style={{ width: 60, textAlign: 'center' }}>Write</th>
              </tr></thead>
              <tbody>
                {users.map((u) => {
                  const g = grants[u.username] ?? { read: false, write: false };
                  return (
                    <tr key={u.username}>
                      <td className="mono" style={{ fontSize: 12.5 }}>{u.username}</td>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={g.read} disabled={g.write}
                               title={g.write ? 'Write includes read' : undefined}
                               onChange={() => toggleUser(u.username, 'read')}/>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={g.write} onChange={() => toggleUser(u.username, 'write')}/>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {!newUserOpen ? (
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy}
                    onClick={() => setNewUserOpen(true)}>
              <I.Plus size={13}/> New basic-auth user
            </button>
          ) : (
            <div className="card card-pad" style={{ background: 'var(--bg-1)', marginBottom: 10 }}>
              <div className="spread" style={{ marginBottom: 8 }}>
                <div className="field-label" style={{ margin: 0 }}>New basic-auth user</div>
                <button type="button" className="btn btn-ghost btn-sm" disabled={busy}
                        onClick={() => { setNewUserOpen(false); setNewUsername(''); setNewPassword(''); }}>
                  Remove
                </button>
              </div>
              <div className="row" style={{ gap: 6, marginBottom: 8 }}>
                <input className="input" style={{ flex: 1 }} placeholder="username" value={newUsername}
                       onChange={(e) => { setNewUsername(e.target.value); setErr(null); }}/>
                <input className="input" style={{ flex: 1 }} placeholder="password" value={newPassword}
                       onChange={(e) => { setNewPassword(e.target.value); setErr(null); }}/>
                <button type="button" className="btn btn-ghost btn-icon" style={fieldButton} disabled={busy}
                        title="Generate a 12-char password"
                        onClick={() => setNewPassword(generatePassword(12))}>
                  <I.Refresh size={14}/>
                </button>
              </div>
              <div className="row" style={{ gap: 16, fontSize: 12.5 }}>
                <label className="row" style={{ gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={newUserRW.read} disabled={newUserRW.write}
                         title={newUserRW.write ? 'Write includes read' : undefined}
                         onChange={() => setNewUserRW((p) => ({ ...p, read: !p.read, write: !p.read ? p.write : false }))}/>
                  Read
                </label>
                <label className="row" style={{ gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={newUserRW.write}
                         onChange={() => setNewUserRW((p) => ({ write: !p.write, read: !p.write ? true : p.read }))}/>
                  Write
                </label>
              </div>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 8 }}>
                Created in <b>{client.id}</b> and able to sign into its portal, where it starts out seeing
                <b> no services</b>; <b>S3 access</b> on the user, on the Overview tab, turns that on. The
                username has to be free across every client — the whole cluster shares one namespace.
              </div>
              {newUserPartial && (
                <div className="field-error" style={{ fontSize: 11.5, marginTop: 6 }}>
                  Fill both the username and the password, or remove this user.
                </div>
              )}
            </div>
          )}

          {accounts.length > 0 && (
            <table className="tbl" style={{ marginTop: 10 }}>
              <thead><tr>
                <th>Global S3 account</th>
                <th style={{ width: 60, textAlign: 'center' }}>Read</th>
                <th style={{ width: 60, textAlign: 'center' }}>Write</th>
              </tr></thead>
              <tbody>
                {accounts.map((a) => {
                  const g = accountGrants[a.id] ?? { read: false, write: false };
                  return (
                    <tr key={a.id}>
                      <td style={{ fontSize: 12.5 }}>
                        {a.name}{a.note ? <span className="muted" style={{ fontSize: 11.5 }}> · {a.note}</span> : null}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={g.read} disabled={g.write}
                               title={g.write ? 'Write includes read' : undefined}
                               onChange={() => toggleAccount(a.id, 'read')}/>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={g.write} onChange={() => toggleAccount(a.id, 'write')}/>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {err && <div className="field-error" style={{ fontSize: 12.5 }}>{err}</div>}
      </div>
    </Modal>
  );
}

// fieldButton makes a button sitting beside an input-like field match that
// field's height instead of keeping `.btn`'s fixed one, which would leave it
// short and floating above the field's centre line.
const fieldButton: React.CSSProperties = { height: 'auto', alignSelf: 'stretch' };

// BucketCredentialsModal shows a connection's endpoint, bucket and keys with
// copy buttons. It serves three moments: straight after provisioning, after a
// key rotation, and any later reveal from the table. Only the wording differs —
// a freshly issued key is worth stopping for, an old one isn't.
function BucketCredentialsModal({ result, mode, onClose, onManageAccess, onRotate }: {
  result: ProvisionedBucket | null;
  mode: 'created' | 'rotated' | 'view';
  onClose: () => void;
  onManageAccess?: () => void;
  onRotate?: () => void;
}) {
  const toast = useToast();
  // The secret starts masked on a plain reveal — the operator may well be
  // sharing their screen — but stays visible when it was just generated.
  const [show, setShow] = useState(mode !== 'view');
  useEffect(() => { setShow(mode !== 'view'); }, [mode, result]);

  // Copying can fail for reasons the operator cannot see: the clipboard API is
  // absent entirely outside a secure context (an admin reached over plain http
  // on a LAN address), and a write can be rejected. Silence there is the worst
  // outcome — the dialog looks identical to success and a stale clipboard gets
  // pasted in place of the secret.
  const copy = (label: string, value: string) => {
    if (!navigator.clipboard?.writeText) {
      toast?.error('Could not copy', 'This browser only allows the clipboard over HTTPS — select the value and copy it manually.');
      return;
    }
    navigator.clipboard.writeText(value)
      .then(() => toast?.success('Copied', label))
      .catch((e) => toast?.error('Could not copy', e instanceof Error ? e.message : String(e)));
  };

  const title = mode === 'created' ? 'Bucket ready — here are the credentials'
              : mode === 'rotated' ? 'New secret key issued'
              : 'Connection credentials';
  const subtitle = mode === 'created' ? 'Give these to the client. You can look them up again later from the table.'
                 : mode === 'rotated' ? 'The previous secret key stopped working. Anything still using it must be updated to this one.'
                 : 'Looking these up is recorded in the audit log.';

  const rows: [string, string, boolean][] = result
    ? [['Endpoint', result.endpoint, false], ['Bucket', result.bucket, false],
       ['Access key', result.accessKey, false], ['Secret key', result.secretKey, true]]
    : [];

  return (
    <Modal open={result !== null} onClose={onClose} title={title} subtitle={subtitle}
           footer={
             <>
               {/* Replacing the key belongs with the key itself: the operator
                   who just looked at a secret is the one deciding it leaked. */}
               {onRotate && <button className="btn btn-danger" onClick={onRotate}>Issue a new secret key</button>}
               {onManageAccess && <button className="btn" onClick={onManageAccess}>Give users access…</button>}
               <button className="btn btn-primary" onClick={onClose}>Done</button>
             </>
           }>
      {result && (
        <div className="col" style={{ gap: 8 }}>
          {rows.map(([label, value, secret]) => (
            <div key={label}>
              <div className="field-label" style={{ marginBottom: 4 }}>{label}</div>
              {/* `.btn` sets a fixed height, which wins over the row's stretch
                  and leaves the buttons short and riding above the field's
                  centre line. Overriding it to auto lets them stretch to
                  exactly the field's height, whatever that turns out to be. */}
              <div className="row" style={{ gap: 6, alignItems: 'stretch' }}>
                <div className="mono" style={{ flex: 1, padding: '9px 10px', background: 'var(--bg-3)',
                                               borderRadius: 6, fontSize: 12, wordBreak: 'break-all',
                                               userSelect: secret && !show ? 'none' : 'all' }}>
                  {secret && !show ? '•'.repeat(24) : value}
                </div>
                {secret && (
                  <button className="btn btn-sm" style={fieldButton} title={show ? 'Hide' : 'Show'}
                          onClick={() => setShow((v) => !v)}>
                    {show ? <I.EyeOff size={12}/> : <I.Eye size={12}/>}
                  </button>
                )}
                {/* Copy works while masked — reading it off the screen is the
                    part worth avoiding, not putting it on the clipboard. */}
                <button className="btn btn-sm" style={fieldButton} title={`Copy ${label.toLowerCase()}`}
                        onClick={() => copy(label, value)}>
                  <I.Copy size={12}/>
                </button>
              </div>
            </div>
          ))}
          <button className="btn btn-sm" style={{ alignSelf: 'flex-start', marginTop: 2 }}
                  onClick={() => copy('All credentials',
                    `Endpoint: ${result.endpoint}\nBucket: ${result.bucket}\nAccess key: ${result.accessKey}\nSecret key: ${result.secretKey}`)}>
            <I.Copy size={12}/> Copy all
          </button>
        </div>
      )}
    </Modal>
  );
}


// UsageBar shows consumption against the quota. Unlimited buckets show the size
// alone — a bar with no ceiling would be meaningless.
function UsageBar({ usage }: { usage: BucketUsage | undefined }) {
  if (!usage) return <span className="muted" style={{ fontSize: 12 }}>—</span>;
  if (usage.quota <= 0) {
    return <span className="mono" style={{ fontSize: 12 }}>{fmtBytes(usage.bytes)} <span className="muted">· no limit</span></span>;
  }
  const pct = Math.min(100, Math.round((usage.bytes / usage.quota) * 100));
  const color = pct >= 90 ? 'var(--red)' : pct >= 70 ? 'var(--amber)' : 'var(--accent)';
  return (
    <div style={{ minWidth: 120 }}>
      <div className="mono" style={{ fontSize: 11.5, marginBottom: 3 }}>
        {fmtBytes(usage.bytes)} <span className="muted">/ {fmtBytes(usage.quota)}</span>
      </div>
      <div style={{ height: 4, background: 'var(--bg-3)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color }}/>
      </div>
      {/* Over the limit the client's uploads start failing with a permission
          error. Saying so here is what connects that to this number. */}
      {usage.writesPaused && (
        <div style={{ fontSize: 11, marginTop: 4, color: 'var(--red)' }}
             title="Uploads are refused while the bucket is over its limit. Reading and deleting still work — delete something and uploads come back on their own.">
          uploads paused · over limit
        </div>
      )}
    </div>
  );
}

function S3Card({ client, managedStorage }: { client: Client; managedStorage: boolean }) {
  const toast = useToast();
  const [rows, setRows] = useState<S3Config[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState<S3Config | null>(null);
  const [grantsConfig, setGrantsConfig] = useState<S3Config | null>(null);
  const [confirmDel, setConfirmDel] = useState<S3Config | null>(null);
  // Deleting a managed bucket destroys data, so the dialog holds the typed
  // confirmation and blocks itself while the request is in flight — a second
  // click must not fire a second destroy.
  const [delConfirmText, setDelConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [newBucketOpen, setNewBucketOpen] = useState(false);
  const [credentials, setCredentials] = useState<ProvisionedBucket | null>(null);
  const [credMode, setCredMode] = useState<'created' | 'rotated' | 'view'>('created');
  // The row the credentials dialog is showing. A reveal response carries no
  // connection object, so rotating from inside that dialog needs the row kept
  // alongside it rather than read back out of the response.
  const [credRow, setCredRow] = useState<S3Config | null>(null);
  const [confirmRotate, setConfirmRotate] = useState<S3Config | null>(null);
  // Live consumption per managed connection id. Read from the storage server,
  // so it is fetched separately from the rows and allowed to fail quietly.
  const [usage, setUsage] = useState<Record<string, BucketUsage>>({});

  // loadUsage runs after the rows land. A bucket whose usage can't be read is
  // simply left out of the map (the column shows "—") — a storage hiccup must
  // not blank out the connection list itself.
  const loadUsage = (list: S3Config[]) => {
    const managed = list.filter((c) => c.managed);
    if (managed.length === 0) return;
    Promise.all(managed.map((c) =>
      fetchBucketUsage(client.id, c.id).then((u) => [c.id, u] as const).catch(() => null),
    )).then((res) => {
      const next: Record<string, BucketUsage> = {};
      for (const r of res) if (r) next[r[0]] = r[1];
      setUsage(next);
    });
  };

  const reload = () => {
    setLoadErr(null);
    // A load error must not masquerade as "no connections" (which invites the
    // operator to re-add ones that already exist). Surface it distinctly.
    fetchS3Configs(client.id).then((r) => { setRows(r); loadUsage(r); }).catch((e) => { setLoadErr(e instanceof ApiError ? e.message : String(e)); setRows([]); });
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [client.id]);

  const showCredentials = async (row: S3Config) => {
    try {
      const res = await fetchS3Credentials(client.id, row.id);
      setCredMode('view');
      setCredRow(row);
      setCredentials(res);
    } catch (err) {
      toast?.error('Could not load credentials', err instanceof ApiError ? err.message : String(err));
    }
  };

  const rotate = async (row: S3Config) => {
    try {
      const res = await rotateS3BucketKeys(client.id, row.id);
      setCredMode('rotated');
      setCredRow(row);
      setCredentials(res);
      toast?.success('New key issued', `${row.name} · the previous key no longer works`);
    } catch (err) {
      toast?.error('Could not issue a new key', err instanceof ApiError ? err.message : String(err));
    }
  };

  const add = async (v: S3ConfigInput) => {
    try {
      await createS3Config(client.id, v);
      toast?.success('S3 connection added', v.name);
      reload();
    } catch (err) {
      toast?.error('Could not add connection', err instanceof ApiError ? err.message : String(err));
      throw err;
    }
  };

  const edit = async (v: S3ConfigInput) => {
    if (!editRow) return;
    try {
      await updateS3Config(client.id, editRow.id, v);
      toast?.success('S3 connection updated', v.name);
      reload();
    } catch (err) {
      toast?.error('Could not update connection', err instanceof ApiError ? err.message : String(err));
      throw err;
    }
  };

  const remove = async (row: S3Config) => {
    try {
      await deleteS3Config(client.id, row.id);
      toast?.success(row.managed ? 'Bucket deleted' : 'Removed',
        row.managed ? `${row.bucket} and its contents are gone` : row.name);
      reload();
    } catch (err) {
      toast?.error('Delete failed', err instanceof ApiError ? err.message : String(err));
    }
  };

  // How much the pending delete would destroy. Read fresh when the dialog opens
  // rather than borrowed from the table's map: that map fills in lazily (one
  // `du` per bucket) and is often still empty at this moment, which would have
  // the dialog claim the contents "could not be read" when it simply had not
  // read them yet — the one place where guessing is least acceptable.
  const [delUsage, setDelUsage] = useState<{ state: 'loading' | 'ok' | 'error'; value?: BucketUsage }>({ state: 'loading' });
  useEffect(() => {
    if (!confirmDel?.managed || !managedStorage) return;
    let cancelled = false;
    const known = usage[confirmDel.id];
    setDelUsage(known ? { state: 'ok', value: known } : { state: 'loading' });
    fetchBucketUsage(client.id, confirmDel.id)
      .then((u) => { if (!cancelled) setDelUsage({ state: 'ok', value: u }); })
      .catch(() => { if (!cancelled) setDelUsage((p) => (p.state === 'ok' ? p : { state: 'error' })); });
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [confirmDel, client.id, managedStorage]);

  // Stable `initial` for the edit modal: only changes when the edited row does,
  // not on every render — so a background re-render doesn't hand the modal a new
  // object identity (see the modal's re-seed guard).
  const editInitial = useMemo<S3ConfigInput>(() => editRow
    ? { name: editRow.name, note: editRow.note, endpoint: editRow.endpoint, accessKey: editRow.accessKey,
        secretKey: editRow.secretKey, region: editRow.region, bucket: editRow.bucket }
    : EMPTY_S3, [editRow]);

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-head">
        <div>
          <h3>S3 connections</h3>
          <p>Object storage for this client · grant per-user or external-account read/write via the Access button</p>
        </div>
        <div className="row" style={{ gap: 6 }}>
          {/* Registering someone else's bucket stays available, but creating one
              on our own storage is the primary action where it's supported. */}
          <button className={managedStorage ? 'btn btn-sm' : 'btn btn-primary btn-sm'} onClick={() => setAddOpen(true)}>
            <I.Plus size={14}/> Add connection
          </button>
          {managedStorage && (
            <button className="btn btn-primary btn-sm" onClick={() => setNewBucketOpen(true)}
                    title="Create a bucket on the platform's storage, with its own user and size limit">
              <I.Plus size={14}/> New bucket
            </button>
          )}
        </div>
      </div>

      {/* A long connection name widened the table past its card, and because
          nothing scrolled, the row's action buttons ended up off-screen and
          literally unclickable — the row could not even be deleted. The table
          scrolls inside its own box now, and the name cell truncates. */}
      <div style={{ overflowX: 'auto' }}>
      <table className="tbl">
        <thead><tr>
          <th>Name</th><th>Type</th><th>Bucket</th><th>Usage</th><th>Endpoint</th><th style={{ width: 72 }}></th>
        </tr></thead>
        <tbody>
          {rows === null && !loadErr && (
            <tr><td colSpan={6} className="muted" style={{ padding: 14, fontSize: 12.5 }}>Loading…</td></tr>
          )}
          {loadErr && (
            <tr><td colSpan={6} className="field-error" style={{ padding: 14, fontSize: 12.5 }}>Couldn't load S3 connections: {loadErr}</td></tr>
          )}
          {!loadErr && rows?.length === 0 && (
            <tr><td colSpan={6} className="muted" style={{ padding: 14, fontSize: 12.5 }}>
              No S3 storage yet — {managedStorage ? <>create one with <b>New bucket</b>, or register an external one with <b>Add connection</b>.</> : <>add one with <b>Add connection</b>.</>}
            </td></tr>
          )}
          {rows?.map((c) => (
            <tr key={c.id}>
              <td style={{ fontSize: 12.5, fontWeight: 600, maxWidth: 220 }}>
                {c.name}
                {c.note && (
                  // Clamped to two lines: an unclamped note made one row five
                  // lines tall and pushed the rest of the table out of view.
                  <div className="muted" title={c.note}
                       style={{ fontSize: 11, fontWeight: 400, whiteSpace: 'normal', display: '-webkit-box',
                                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {c.note}
                  </div>
                )}
              </td>
              <td>
                <span className={c.managed ? 'badge badge-accent' : 'badge'}
                      title={c.managed
                        ? 'Provisioned on the platform storage: bucket, user and size limit are real'
                        : 'Externally created storage — this is only its recorded address'}>
                  {c.managed ? 'managed' : 'external'}
                </span>
              </td>
              <td className="mono" style={{ fontSize: 12 }}>
                {c.bucket || '—'}
                {/* Only for a registered server: a bucket on the deployment's own
                    one is the norm and labelling every row would be noise. */}
                {c.minioServerName && (
                  <div className="muted" style={{ fontSize: 11 }}>on {c.minioServerName}</div>
                )}
              </td>
              <td>{c.managed ? <UsageBar usage={usage[c.id]}/> : <span className="muted" style={{ fontSize: 12 }}>—</span>}</td>
              <td className="mono muted" style={{ fontSize: 12, maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={c.endpoint}>{c.endpoint || '—'}</td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {/* Endpoint, keys and issuing a replacement all live behind this
                    one button; the size limit is edited with the name. */}
                {/* The connection-side view: who may use THIS connection.
                    External S3 accounts can only be granted here. Its mirror
                    image — which connections one user may use — is the S3
                    access button on the user, on the Overview tab. */}
                <button className="btn btn-sm" title="Manage which users & external accounts can access this connection"
                        onClick={() => setGrantsConfig(c)}
                        style={{ background: 'var(--accent-soft)', color: 'var(--accent-hi)',
                                 border: '1px solid var(--accent-ring)', gap: 5, marginRight: 6 }}>
                  <I.Users size={13}/> Access
                </button>
                <button className="btn btn-ghost btn-icon btn-sm" title="Endpoint & keys"
                        onClick={() => showCredentials(c)}>
                  <I.Key size={13}/>
                </button>
                <button className="btn btn-ghost btn-icon btn-sm" title="Edit" onClick={() => setEditRow(c)}>
                  <I.Pencil size={13}/>
                </button>
                <button className="btn btn-ghost btn-icon btn-sm" title="Delete" onClick={() => setConfirmDel(c)}>
                  <I.Trash size={13}/>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <S3ConnectionGrantsModal open={!!grantsConfig} onClose={() => setGrantsConfig(null)} client={client} config={grantsConfig}/>
      <S3FormModal open={addOpen} onClose={() => setAddOpen(false)} title="Add S3 connection"
                   initial={NEW_S3} onSave={add}/>
      <S3FormModal open={!!editRow} onClose={() => setEditRow(null)} title="Edit S3 connection"
                   initial={editInitial} onSave={edit} nameOnly={!!editRow?.managed}
                   // The limit is only ours to set on storage we provisioned, and
                   // only while the storage server is actually configured.
                   quota={editRow?.managed && managedStorage
                     ? { bytes: editRow.quotaBytes, onSave: (b) => setBucketQuota(client.id, editRow.id, b).then(() => { toast?.success('Limit updated', b > 0 ? fmtBytes(b) : 'unlimited'); }) }
                     : undefined}/>

      <NewBucketModal open={newBucketOpen} onClose={() => setNewBucketOpen(false)} client={client}
                      onCreated={(res) => { setNewBucketOpen(false); setCredMode('created'); setCredentials(res); reload(); }}/>
      <BucketCredentialsModal result={credentials} mode={credMode} onClose={() => setCredentials(null)}
                              onManageAccess={credMode === 'created' && credentials?.connection
                                ? () => { const c = credentials.connection; setCredentials(null); setGrantsConfig(c); }
                                : undefined}
                              // Only on a reveal: right after a bucket is created or a key
                              // is issued, the shown secret IS the current one — replacing
                              // it in that moment is never what the operator means. And only
                              // for storage we provisioned: this dialog also opens for
                              // externally-registered connections, whose keys are not ours
                              // to replace (the endpoint would refuse anyway).
                              onRotate={credMode === 'view' && credRow?.managed && managedStorage
                                ? () => setConfirmRotate(credRow)
                                : undefined}/>

      <MiniModal open={!!confirmRotate} onClose={() => setConfirmRotate(null)}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Issue a new secret key?</div>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-2)' }}>
          The current key for <span className="mono">{confirmRotate?.name}</span> stops working immediately —
          anything already using it will break until it gets the new one. The access key stays the same.
        </p>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setConfirmRotate(null)}>Cancel</button>
          <button className="btn btn-primary btn-sm"
                  onClick={() => { const c = confirmRotate; setConfirmRotate(null); if (c) rotate(c); }}>Issue new key</button>
        </div>
      </MiniModal>

      <MiniModal open={!!confirmDel} onClose={() => { if (!deleting) { setConfirmDel(null); setDelConfirmText(''); } }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
          {confirmDel?.managed ? 'Delete this bucket and everything in it?' : 'Delete connection?'}
        </div>
        {confirmDel?.managed ? (
          <>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-2)' }}>
              Deletes the bucket <span className="mono">{confirmDel.bucket}</span> from the storage server
              along with <b>every object in it</b>, and revokes its keys. {
                delUsage.state === 'loading' ? <>Checking what is in it…</>
                : delUsage.state === 'ok' && delUsage.value
                  ? <>It holds <b>{delUsage.value.objects} object{delUsage.value.objects === 1 ? '' : 's'}</b> ({fmtBytes(delUsage.value.bytes)}).</>
                  : <>Its contents could not be read, so this may be more data than you expect.</>}
              {' '}<b>There is no undo and no backup.</b>
            </p>
            {/* Typing the name is the point: it makes the operator look at WHICH
                bucket this is, which a second button never does. */}
            <div style={{ marginTop: 12 }}>
              <label className="field-label">Type <span className="mono">{confirmDel.bucket}</span> to confirm</label>
              <input className="input input-mono" style={{ width: '100%' }} autoFocus autoComplete="off"
                     value={delConfirmText} disabled={deleting}
                     onChange={(e) => setDelConfirmText(e.target.value)}/>
            </div>
          </>
        ) : (
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-2)' }}>
            Remove <span className="mono">{confirmDel?.name}</span>. This only removes the record here —
            storage created elsewhere is untouched.
          </p>
        )}
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="btn btn-ghost btn-sm" disabled={deleting}
                  onClick={() => { setConfirmDel(null); setDelConfirmText(''); }}>Cancel</button>
          <button className="btn btn-danger-solid btn-sm"
                  disabled={deleting || (!!confirmDel?.managed && delConfirmText.trim() !== confirmDel.bucket)}
                  onClick={async () => {
                    const c = confirmDel; if (!c) return;
                    setDeleting(true);
                    await remove(c);
                    setDeleting(false); setConfirmDel(null); setDelConfirmText('');
                  }}>
            {deleting ? 'Deleting…' : confirmDel?.managed ? 'Delete bucket and data' : 'Delete'}
          </button>
        </div>
      </MiniModal>
    </div>
  );
}

function ClientDangerZone({
  client, setState, onNav,
}: {
  client: Client;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
  onNav: (r: Route) => void;
}) {
  const toast = useToast();
  const [confirmDel, setConfirmDel] = useState(false);
  const [confirmName, setConfirmName] = useState('');

  const [deleting, setDeleting] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);

  const isSuspended = client.status === 'suspended' as Client['status'];

  const toggleSuspend = async () => {
    setConfirmSuspend(false);
    setSuspending(true);
    let updated: Client;
    try {
      updated = isSuspended ? await resumeTenant(client.id) : await suspendTenant(client.id);
    } catch (err) {
      setSuspending(false);
      toast?.error(isSuspended ? 'Resume failed' : 'Suspend failed',
                   err instanceof ApiError ? err.message : String(err));
      return;
    }
    setState((s) => ({
      ...s,
      clients: s.clients.map((c) => c.id === client.id ? updated : c),
    }));
    setSuspending(false);
    toast?.success(isSuspended ? 'Tenant resumed' : 'Tenant suspended',
                   `${client.name} ${isSuspended ? 'is back online' : 'is now suspended'}`);
  };

  const deleteClient = async () => {
    setDeleting(true);
    try {
      await deleteTenant(client.id);
    } catch (err) {
      setDeleting(false);
      const msg = err instanceof ApiError ? err.message : String(err);
      toast?.error('Delete failed', msg);
      return;
    }
    setState((s) => ({
      ...s,
      clients: s.clients.filter((c) => c.id !== client.id),
    }));
    setDeleting(false);
    toast?.success('Client moved to Trash', `${client.name} is recoverable for 30 days`);
    onNav({ page: 'clients' });
  };

  return (
    <div className="card">
      <div className="card-head"><div><h3 style={{ color: 'var(--red)' }}>Danger zone</h3><p>Destructive operations — recoverable for a short window</p></div></div>
      <div className="card-pad col" style={{ gap: 14 }}>
        <div className="spread" style={{ alignItems: 'flex-start' }}>
          <div style={{ maxWidth: 480 }}>
            <div style={{ fontWeight: 500, fontSize: 13 }}>{isSuspended ? 'Resume tenant' : 'Suspend tenant'}</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              {isSuspended
                ? 'Restore each service to its pre-suspend replica count and bring the tenant back to ready.'
                : 'Scale all services to 0 replicas. The current replica counts are snapshotted so Resume restores them. Repo & PVCs are kept.'}
            </div>
          </div>
          <button className={'btn ' + (isSuspended ? 'btn-primary' : 'btn-danger')}
                  disabled={suspending} onClick={() => isSuspended ? toggleSuspend() : setConfirmSuspend(true)}>
            {suspending ? (isSuspended ? 'Resuming…' : 'Suspending…') : (isSuspended ? 'Resume' : 'Suspend')}
          </button>
        </div>
        <div className="divider"></div>
        <div className="spread" style={{ alignItems: 'flex-start' }}>
          <div style={{ maxWidth: 480 }}>
            <div style={{ fontWeight: 500, fontSize: 13 }}>Delete tenant</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Soft-delete: the namespace, ArgoCD apps and git folder are torn down, but the tenant is recoverable from{' '}
              <button className="btn btn-ghost btn-sm" style={{ height: 'auto', padding: 0, color: 'var(--text-1)', textDecoration: 'underline' }}
                      onClick={() => onNav({ page: 'trash' })}>Trash</button>{' '}
              for 30 days. After that the pruner hard-deletes the row.
            </div>
          </div>
          <button className="btn btn-danger-solid" onClick={() => setConfirmDel(true)}>
            <I.Trash size={13}/> Delete client
          </button>
        </div>
      </div>

      <MiniModal open={confirmSuspend} onClose={() => { if (!suspending) setConfirmSuspend(false); }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Suspend this tenant?</div>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-2)' }}>
          Scales all {client.services.length} {client.services.length === 1 ? 'service' : 'services'} of{' '}
          <span className="mono">{client.name}</span> to 0 replicas — their sites go offline. Replica counts are snapshotted; Resume restores them.
        </p>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="btn btn-ghost btn-sm" disabled={suspending} onClick={() => setConfirmSuspend(false)}>Cancel</button>
          <button className="btn btn-danger-solid btn-sm" disabled={suspending} onClick={toggleSuspend}>
            {suspending ? 'Suspending…' : 'Suspend'}
          </button>
        </div>
      </MiniModal>

      <Modal open={confirmDel} onClose={() => { setConfirmDel(false); setConfirmName(''); }}
             title="Delete this tenant?"
             subtitle="Recoverable from Trash for 30 days. Type the client name to confirm."
             footer={
               <>
                 <button className="btn" onClick={() => { setConfirmDel(false); setConfirmName(''); }}>Cancel</button>
                 <button className="btn btn-danger-solid"
                         disabled={confirmName !== client.name || deleting}
                         onClick={deleteClient}>
                   <I.Trash size={13}/> {deleting ? 'Deleting…' : 'I understand, delete'}
                 </button>
               </>
             }>
        <div className="col" style={{ gap: 10 }}>
          <div className="muted" style={{ fontSize: 12.5 }}>
            Type <span className="mono" style={{ color: 'var(--text-1)' }}>{client.name}</span> to confirm:
          </div>
          <input className="input input-mono" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={client.name}/>
          <div style={{ padding: 10, background: 'var(--red-bg)', borderRadius: 6, fontSize: 12, color: 'var(--red)' }}>
            ⚠ Tears down {client.services.length} {client.services.length === 1 ? 'service' : 'services'}, the namespace and the git folder. Restorable from Trash within 30 days; after that the pruner hard-deletes the row and PVCs.
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── ADD CLIENT WIZARD ──────────────────────────────────────────────────────
type Phase = { id: string; label: string; meta: string };

export function AddClientWizard({
  open, onClose, state, setState, onNav,
}: {
  open: boolean;
  onClose: () => void;
  state: AppState;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
  onNav: (r: Route) => void;
}) {
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [catalog, setCatalog] = useState<ChartCatalogEntry[]>([]);
  const [catalogErr, setCatalogErr] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    displayName: '',
    plan: state.settings.defaultPlan as Plan,
    contactEmail: '',
    services: [] as string[],
  });
  const [provisioning, setProvisioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Real provisioning phases — what actually happens server-side, in order.
  // Each one reflects a concrete operation we can observe via API calls,
  // not a fake sleep with a checkmark.
  type RealPhase = { id: string; label: string; meta: string };
  const phases: RealPhase[] = [
    { id: 'db',      label: 'Register the client and its services',    meta: 'db' },
    { id: 'git',     label: 'Commit the client’s config to Git',  meta: 'git' },
    { id: 'argo',    label: 'ArgoCD picks up the change',              meta: 'argocd' },
    { id: 'sync',    label: 'Services roll out and become healthy',    meta: 'argocd' },
  ];
  const [phaseStatus, setPhaseStatus] = useState<Record<string, 'running' | 'done' | 'failed' | 'timeout'>>({});
  const [phaseDetail, setPhaseDetail] = useState<Record<string, string>>({});

  // Fetch the live chart catalog whenever the wizard opens. The list comes
  // from BE's helmCatalog (charts vendored into git under /charts) — keeps
  // the wizard truthful: whatever you see here is what the system can
  // actually provision.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCatalogErr(null);
    fetchChartCatalog()
      .then((c) => {
        if (cancelled) return;
        setCatalog(c);
        // Default-select every catalog entry so a "next > next > provision"
        // flow gives the user a tenant with a working starter stack.
        setForm((f) => ({ ...f, services: c.map((x) => x.name) }));
      })
      .catch((e) => { if (!cancelled) setCatalogErr(e instanceof ApiError ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStep(0);
      setForm({
        name: '',
        displayName: '',
        plan: state.settings.defaultPlan,
        contactEmail: '',
        services: catalog.map((c) => c.name),
      });
      setProvisioning(false);
      setPhaseStatus({});
      setPhaseDetail({});
      setError(null);
    }
  }, [open]);

  const stepNames = ['Identity', 'Services', 'Review', 'Provision'];
  const nameValid = /^[a-z][a-z0-9-]{2,29}$/.test(form.name);
  const nameTaken = state.clients.some((c) => c.id === form.name);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail);

  const canNext0 = nameValid && !nameTaken && emailValid;
  const canNext1 = true; // services are optional — a tenant may be created bare

  const next = () => {
    if (step === 2) startProvision();
    else setStep(step + 1);
  };

  const startProvision = () => {
    setStep(3);
    setProvisioning(true);
    runPhases();
  };

  // runPhases drives the real provisioning flow against the backend. Each
  // phase here corresponds to an observable server-side action — no faked
  // sleeps. Failures surface in the UI as a red badge on the affected phase
  // and a retry path back to the Review step.
  const runPhases = async () => {
    setError(null);
    setPhaseStatus({});
    setPhaseDetail({});

    const mark = (id: string, status: 'running' | 'done' | 'failed' | 'timeout', detail?: string) => {
      setPhaseStatus((s) => ({ ...s, [id]: status }));
      if (detail !== undefined) setPhaseDetail((s) => ({ ...s, [id]: detail }));
    };

    // Phase 1+2: POST /tenants. BE atomically inserts the tenant + service
    // rows (phase "db") and commits the rendered manifests to git (phase
    // "git"). We collapse the call into both phase markers since BE doesn't
    // stream progress — when the response lands, both are done.
    mark('db', 'running');
    mark('git', 'running', 'pushing to git…');
    let created: Client;
    try {
      created = await createTenant({
        clientId: form.name,
        displayName: form.displayName || form.name,
        plan: form.plan,
        contactEmail: form.contactEmail,
        // No `branch`: per ADR-0001, every tenant tracks the cluster-wide
        // GitOps branch; BE fills it from gitops config.
        services: form.services,
      });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      mark('db', 'failed', msg);
      mark('git', 'failed', '');
      setError(msg);
      setProvisioning(false);
      setStep(2);
      return;
    }
    mark('db', 'done', `client + ${created.services.length} services registered`);
    mark('git', 'done', `pushed (commit will show in Audit tab)`);

    // Phase 3: ArgoCD picks up the new tenant directory. We can detect this
    // by polling /argocd-status — once Exists=true, the parent Application
    // CR exists; refining further into Healthy is phase 4.
    mark('argo', 'running', 'waiting for ApplicationSet generator…');
    const argoReady = await pollUntil(
      () => fetchArgocdStatus(form.name).then((s) => s.exists ? s : null),
      { intervalMs: 1500, timeoutMs: 60_000 },
    );
    if (!argoReady) {
      mark('argo', 'timeout', 'ApplicationSet still creating App — check ArgoCD UI');
    } else {
      mark('argo', 'done', `sync=${argoReady.sync}`);
    }

    // Phase 4: ArgoCD reports Synced. We DON'T wait for full Healthy here —
    // some charts (loki StatefulSet) take 3+ minutes for first-pod startup
    // and that's not on the GitOps critical path. Once ArgoCD has Synced
    // the manifests, the rest happens in the cluster and the user can watch
    // it on the client detail screen (pod states animate live there).
    mark('sync', 'running', 'waiting for ArgoCD to confirm Synced…');
    const started = Date.now();
    const synced = await pollUntil(
      async () => {
        const s = await fetchArgocdStatus(form.name);
        const elapsed = Math.round((Date.now() - started) / 1000);
        if (s.sync === 'Synced') return s;
        mark('sync', 'running', `${elapsed}s · sync=${s.sync}`);
        return null;
      },
      { intervalMs: 1500, timeoutMs: 60_000 },
    );
    if (!synced) {
      mark('sync', 'timeout', 'ArgoCD still syncing — open in ArgoCD UI for details');
    } else {
      const total = Math.round((Date.now() - started) / 1000);
      const note = synced.health === 'Healthy'
        ? `Healthy after ${total}s`
        : `Synced after ${total}s · pods still starting (watch live on client detail)`;
      mark('sync', 'done', note);
    }

    // Refresh the full list from the backend — that's the canonical source.
    let clients: Client[];
    try {
      clients = await fetchTenants();
    } catch {
      clients = [created, ...state.clients];
    }
    setState((s) => ({ ...s, clients }));
    setProvisioning(false);
  };

  const finish = () => {
    onClose();
    onNav({ page: 'client-detail', id: form.name });
    toast?.success('Client provisioned', `${form.name} is live and synced.`);
  };

  return (
    <Modal open={open} onClose={() => { if (!provisioning) onClose(); }}
           title="Add a new client"
           subtitle={step < 3 ? "We'll fork the base repo, configure it and let ArgoCD do the rest." : null}
           size="lg" dismissable={!provisioning}
           footer={step < 3 ? (
             <>
               <span className="muted" style={{ fontSize: 12, marginRight: 'auto' }}>Step {step + 1} of {stepNames.length}</span>
               {step > 0 && <button className="btn" onClick={() => setStep(step - 1)}>Back</button>}
               <button className="btn" onClick={onClose}>Cancel</button>
               <button className="btn btn-primary" disabled={(step === 0 && !canNext0) || (step === 1 && !canNext1)}
                       onClick={next}>
                 {step === 2 ? <>Provision <I.PlayCirc size={14}/></> : <>Continue <I.ChevronR size={13}/></>}
               </button>
             </>
           ) : provisioning ? (
             <span className="row" style={{ gap: 8, fontSize: 12.5, color: 'var(--text-2)' }}>
               <span className="spinner"></span> Provisioning…
             </span>
           ) : (
             <>
               <span className="muted" style={{ fontSize: 12, marginRight: 'auto', color: 'var(--green)' }}>✓ All phases complete</span>
               <button className="btn btn-primary" onClick={finish}>Open client <I.ArrowR size={13}/></button>
             </>
           )}>
      <Stepper steps={stepNames} current={step}/>

      {step === 0 && (
        <div className="col" style={{ gap: 14 }}>
          <div className="field">
            <label className="field-label">Client slug <span className="req-dot">*</span></label>
            <input className={'input input-mono ' + (form.name && (!nameValid || nameTaken) ? 'input-error' : '')}
                   placeholder="acme-shop"
                   value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase() })}/>
            {form.name && !nameValid && <div className="field-error">Use lowercase letters, digits and dashes (3–30 chars, start with letter).</div>}
            {nameTaken && <div className="field-error">A client with this slug already exists.</div>}
            {(!form.name || (nameValid && !nameTaken)) && <div className="field-hint">Used as namespace name, repo suffix and DNS prefix.</div>}
          </div>
          <div className="res-grid">
            <div className="field">
              <label className="field-label">Display name</label>
              <input className="input" placeholder={form.name || 'Acme Shop'}
                     value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })}/>
            </div>
            <div className="field">
              <label className="field-label">Plan <span className="req-dot">*</span></label>
              <select className="select" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value as Plan })}>
                {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label className="field-label">Contact email <span className="req-dot">*</span></label>
            <input className={'input ' + (form.contactEmail && !emailValid ? 'input-error' : '')}
                   type="email" placeholder="ops@acme.com"
                   value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}/>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="col" style={{ gap: 12 }}>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Pick which services to provision. List comes from the live chart catalog in git
            ({catalog.length > 0 ? <span className="mono">{catalog.length} chart{catalog.length === 1 ? '' : 's'} available</span> : 'loading…'}).
            Services can be added or removed later.
          </p>
          {catalogErr && (
            <div className="badge badge-red">Catalog load failed: {catalogErr}</div>
          )}
          {catalog.length > 0 && (() => {
            const allOn = form.services.length === catalog.length;
            return (
              <div onClick={() => setForm({ ...form, services: allOn ? [] : catalog.map((c) => c.name) })}
                   style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                            padding: '2px 2px', userSelect: 'none' }}>
                <div className={'toggle ' + (allOn ? 'on' : '')}/>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{allOn ? 'Deselect all' : 'Select all'}</span>
                <span className="muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{form.services.length}/{catalog.length} selected</span>
              </div>
            );
          })()}
          <div className="col" style={{ gap: 8 }}>
            {catalog.map((def) => {
              const enabled = form.services.includes(def.name);
              return (
                <div key={def.name}
                     onClick={() => setForm({ ...form, services: enabled ? form.services.filter((n) => n !== def.name) : [...form.services, def.name] })}
                     style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                              border: '1px solid ' + (enabled ? 'var(--accent)' : 'var(--border-strong)'),
                              borderRadius: 8, cursor: 'pointer',
                              background: enabled ? 'var(--accent-soft)' : 'var(--bg-2)' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--bg-3)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: enabled ? 'var(--accent)' : 'var(--text-2)' }}>
                    <ServiceIcon name={def.icon} size={14}/>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="row" style={{ gap: 8 }}>
                      <span className="mono" style={{ fontWeight: 500 }}>{def.label || def.name}</span>
                      <span className="muted mono" style={{ fontSize: 11 }}>{def.chartName} {def.chartVersion}</span>
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {def.group === 'gpu' ? 'GPU-bound inference worker · scheduled on node-gpu-*'
                        : def.group === 'observability' ? 'Observability stack (metrics / logs)'
                        : def.group === 'web' ? 'Web / ingress service'
                        : def.group === 'storage' ? 'Storage backend'
                        : 'Lightweight CPU worker'}
                      <span className="mono" style={{ marginLeft: 8 }}>· default: {def.defaultReplicas}× {def.defaultCpu}/{def.defaultMem}</span>
                    </div>
                  </div>
                  <div className={'toggle ' + (enabled ? 'on' : '')}/>
                </div>
              );
            })}
          </div>
          {form.services.length === 0 &&
            <div className="muted" style={{ fontSize: 12.5 }}>No services selected — the tenant will be created empty; you can add services later.</div>}
        </div>
      )}

      {step === 2 && (
        <div>
          {error && (
            <div style={{ margin: '0 0 12px', padding: 10, background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 6, fontSize: 12.5, color: 'var(--red)' }}>
              <strong>Provision failed:</strong> {error}
            </div>
          )}
          <p className="muted" style={{ margin: '0 0 14px', fontSize: 13 }}>
            Review what will be provisioned. Pressing <strong style={{ color: 'var(--text-1)' }}>Provision</strong> creates a new git repo from the base template and registers an ArgoCD app.
          </p>
          <div className="card" style={{ padding: '14px 18px', background: 'var(--bg-2)' }}>
            <div className="summary-row"><span className="lbl">Client slug</span><span className="val">{form.name}</span></div>
            <div className="summary-row"><span className="lbl">Display name</span><span className="val">{form.displayName || form.name}</span></div>
            <div className="summary-row"><span className="lbl">Plan</span><span className="val">{form.plan}</span></div>
            <div className="summary-row"><span className="lbl">Contact</span><span className="val">{form.contactEmail}</span></div>
            <div className="summary-row"><span className="lbl">Namespace</span><span className="val">tenant-{form.name}</span></div>
            <div className="summary-row"><span className="lbl">Repo</span><span className="val" style={{ wordBreak: 'break-all' }}>{state.settings.baseRepo}</span></div>
            <div className="summary-row"><span className="lbl">Branch</span><span className="val mono">{state.settings.baseBranch} <span className="muted">(cluster-wide)</span></span></div>
            <div className="summary-row"><span className="lbl">Services</span><span className="val">{form.services.length ? form.services.join(', ') : 'none (empty tenant)'}</span></div>
            <div className="summary-row"><span className="lbl">ArgoCD app</span><span className="val">tenant-{form.name}</span></div>
          </div>
          <div className="row" style={{ marginTop: 14, padding: 12, background: 'var(--accent-soft)', borderRadius: 8, fontSize: 12.5, color: 'var(--text-1)' }}>
            <I.Info size={14} style={{ color: 'var(--accent)' }}/>
            <span>Default resource limits will be applied based on plan. You can change them anytime.</span>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="col" style={{ gap: 8 }}>
          {phases.map((p) => {
            const st = phaseStatus[p.id];
            const detail = phaseDetail[p.id];
            const cls = st === 'done' ? 'done'
              : st === 'running' ? 'running'
              : st === 'failed' ? 'failed'
              : st === 'timeout' ? 'timeout'
              : '';
            return (
              <div key={p.id} className={'phase ' + cls}>
                {st === 'done' ? <I.CheckCirc size={16} style={{ color: 'var(--green)' }}/> :
                 st === 'running' ? <span className="spinner"/> :
                 st === 'failed' ? <I.XCirc size={16} style={{ color: 'var(--red)' }}/> :
                 st === 'timeout' ? <I.Warn size={16} style={{ color: 'var(--amber)' }}/> :
                 <span style={{ width: 14, height: 14, borderRadius: 999, border: '1.5px dashed var(--text-faint)' }}/>}
                <div style={{ flex: 1 }}>
                  <div className="row" style={{ gap: 8, justifyContent: 'space-between' }}>
                    <span className="label">{p.label}</span>
                    <span className="meta">{p.meta}</span>
                  </div>
                  {detail && (
                    <div className="muted mono" style={{ fontSize: 11, marginTop: 2 }}>{detail}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
